import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Skeleton,
  SimpleGrid,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChartLine, FlowArrow, Info, ListBullets, WarningCircle } from "@phosphor-icons/react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { ResponsiveSankey } from "@nivo/sankey";
import { chartTheme } from "./chartTheme";
import {
  useGetMultiYearConsumption,
  useGetQuarterlyConsumption,
} from "../../../../../hooks/query/emissions-insight/emissions-insights-query";
import QuarterlyBreakdownTable, { useQuarterlyTableCompare } from "../details/QuarterlyBreakdownTable";
import { DimensionDropdown, QuarterDropdown, YearDropdown } from "../FootPrint";
import useAirUserRoles from "../../../../../utils/AuthUtils";
import DashboardCard from "../../../../ISSB/Dashboard/components/DashboardCard";
import YoYChip from "../YoYChip";
import { toSafariSafeSankey } from "../../../../../utils/sankey";
import { isMixedScopeTwoElectricity, v1PurchasedElectricityKwh } from "../../../../../utils/scopeTwo";
import { decadeTickValues, metricValueFontSize, SYMLOG_CONSTANT } from "../../../../../utils/insightsChartScale";

// ---------------------------------------------------------------------------
// SECR consumption dashboard.
//
// All figures are real, from GET /insights/consumption (energy vectors with
// real calorific-derived kWh, on-site generation per-fuel + certificates, water
// supply/discharge in m³, waste by disposal method + type), honoring both the
// dimension filter and the quarter. Where a figure genuinely can't be computed
// (intensity denominators like FTE / revenue, which the platform doesn't store;
// a fuel with no configured calorific value) the section shows a "Data not
// available" / coverage note rather than inventing a number.
//
// The year + prior-year + three older years are fetched together via
// useGetMultiYearConsumption: index [0] drives the headline, [1] the YoY
// comparison, and all five the energy trend line.
// ---------------------------------------------------------------------------

const SECR_PALETTE = {
  electricity: "#60A5FA",
  gas: "#0E7C66",
  liquidFuels: "#B45309",
  other: "#7C3AED",
  transport: "#34D399",
  stationary: "#DB2777",
  water: "#0EA5E9",
  waste: "#475569",
  emissionsLB: "#475569",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtNum = (v, digits = 0) =>
  v == null || !Number.isFinite(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-GB", { maximumFractionDigits: digits });
const fmtPct = (v, digits = 1) =>
  v == null || !Number.isFinite(Number(v))
    ? "—"
    : `${Number(v).toFixed(digits)}%`;

// Page-wide kWh/MWh unit switch (Section 1's UnitToggle). Every energy figure
// on this page is stored/computed in kWh internally (percentages, YoY %,
// sorting, reconciliation) — only the DISPLAYED absolute number and its unit
// label convert, via this one helper, so no arithmetic elsewhere needs to
// change. Default digits step up for MWh so a small-but-real bucket (e.g.
// 400 kWh = 0.40 MWh) doesn't round away to a misleading "0".
const UNIT_DIVISOR = { kWh: 1, MWh: 1000 };
const toDisplayUnit = (v, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return v;
  return Number(v) / (UNIT_DIVISOR[unit] || 1);
};
const fmtEnergy = (v, unit, digits) =>
  fmtNum(toDisplayUnit(v, unit), digits != null ? digits : (unit === "MWh" ? 2 : 0));

// Real kWh-equivalent for a Scope 1 fuel row. The backend derives `kwh` from the
// fuel's configured calorific value (ESG-3941); it is null when no calorific
// factor exists for the (fuel, measure) pair, in which case the row contributes
// nothing to the kWh total and is flagged so the section can show a coverage note.
const rowKwh = (row) => {
  const kwh = row?.kwh;
  return kwh == null || !Number.isFinite(Number(kwh)) ? 0 : Number(kwh);
};

// True when a Scope 1 fuel row carries a positive consumption but no kWh — i.e.
// its energy equivalent is excluded because no calorific factor is configured.
const isKwhMissing = (row) =>
  (Number(row?.consumption) || 0) > 0 && (row?.kwh == null || !Number.isFinite(Number(row?.kwh)));

// ---------------------------------------------------------------------------
// UI primitives
// ---------------------------------------------------------------------------

const SectionHeading = ({ title, info, dataState }) => (
  <Flex align="center" gap={2} mb={3} flexWrap="wrap">
    <Text fontSize="22px" fontWeight="700" color="darkTeal">{title}</Text>
    {info && (
      <Tooltip label={info} hasArrow placement="top" maxW="320px">
        <Box as="span" color="gray.400" cursor="help">
          <Info size={16} />
        </Box>
      </Tooltip>
    )}
    {dataState && <DataStateBadge state={dataState} />}
  </Flex>
);

// Visual flag at the section header to make data provenance unmistakable.
// state = "real"          → green pill, no caveat
//         "partial"       → amber pill, the section mixes real + missing
//         "missing"       → red pill, nothing real
//         "derived"       → grey pill, real-data-derived (no direct field)
const DataStateBadge = ({ state }) => {
  const config = {
    real:    { bg: "green.50",  color: "green.700",  label: "Finalised data" },
    partial: { bg: "orange.50", color: "orange.700", label: "Partial data" },
    missing: { bg: "red.50",    color: "red.700",    label: "Data not available" },
    derived: { bg: "gray.100",  color: "gray.700",   label: "Derived" },
    loading: { bg: "blue.50",   color: "blue.700",   label: "Loading…" },
  };
  const c = config[state] || config.derived;
  return (
    <HStack spacing={1} bg={c.bg} color={c.color} px={2} py={0.5} borderRadius="full">
      {state === "missing" && <WarningCircle size={11} weight="fill" />}
      <Text fontSize="11px" fontWeight="600">{c.label}</Text>
    </HStack>
  );
};

const NotAvailableNote = ({ children }) => (
  <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" px={3} py={2} mb={3}>
    <HStack spacing={2} align="flex-start">
      <Box color="orange.600" mt="2px"><WarningCircle size={14} weight="fill" /></Box>
      <Text fontSize="12px" color="orange.800">{children}</Text>
    </HStack>
  </Box>
);

// Shown instead of a section's real content (and instead of its "no data"
// empty state) while the underlying /insights/consumption query is still on
// its FIRST fetch for the current year/dimension/quarter combination — so a
// slow initial load doesn't read as "this org has no data." Once the query
// resolves, sections fall back to their normal real/missing rendering
// (react-query's `isLoading` is specifically "no cached data yet," so
// switching back to an already-fetched period skips the skeleton and shows
// cached data immediately, as expected).
// `variant` picks a shape roughly matching what each section normally
// renders: "cards" = the Section 1 headline grid, "ring" = donut + list +
// 2 metric cards (Electricity/Gas/Liquid fuels/Other/Transport/Stationary),
// "bars" = Water's 3-card row, "table" = Onsite generation.
const SectionSkeleton = ({ variant = "ring" }) => {
  if (variant === "cards") {
    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height="96px" borderRadius="md" />)}
      </SimpleGrid>
    );
  }
  if (variant === "bars") {
    return (
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height="88px" borderRadius="md" />)}
      </SimpleGrid>
    );
  }
  if (variant === "table") {
    return <Skeleton height="280px" borderRadius="md" />;
  }
  // "ring" (default)
  return (
    <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
      <Skeleton height="380px" borderRadius="md" />
      <Skeleton height="380px" borderRadius="md" />
    </Grid>
  );
};

// Computes the percentage change between current and prior. Returns null
// when either side is missing or prior is zero so the YoY pill is hidden
// rather than rendering a misleading +∞%.
const yoyPct = (current, prior) => {
  const c = Number(current);
  const p = Number(prior);
  if (!Number.isFinite(c) || !Number.isFinite(p)) return null;
  if (p === 0) return null;
  return ((c - p) / p) * 100;
};

const MetricCard = ({
  accent,
  label,
  value,
  unit,
  sublabel,
  missing,
  // Pass a numeric current + prior to get the directional YoY pill
  // rendered alongside the value. Pass `lowerIsBetter` = false for
  // metrics where an increase is the desired direction (e.g. Renewable %,
  // EV %, Recycled). Default = true (lower consumption / waste = better).
  currentNumeric,
  priorNumeric,
  lowerIsBetter = true,
  // How the YoY pill's hover tooltip should render the prior value.
  // `priorDigits` defaults to 0 for kWh-style metrics; pass a value when
  // the metric is a percentage / has decimals. `priorLabel` is the FY
  // label shown in the tooltip — defaults to "Prior year".
  priorDigits = 0,
  priorLabel = "Prior year",
}) => {
  const pct = yoyPct(currentNumeric, priorNumeric);
  const priorDisplay = pct != null
    ? (unit === "%" || (typeof value === "string" && value.includes("%")))
        ? fmtPct(priorNumeric, priorDigits === 0 ? 1 : priorDigits)
        : `${fmtNum(priorNumeric, priorDigits)}${unit ? ` ${unit}` : ""}`
    : null;
  return (
    <Box
      position="relative"
      pt={3}
      bg={missing ? "gray.50" : "white"}
      border="1px solid"
      borderColor={missing ? "gray.200" : "gray.200"}
      borderRadius="md"
      px={4}
      pb={4}
      overflow="hidden"
      opacity={missing ? 0.7 : 1}
    >
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={missing ? "gray.300" : (accent || "gray.300")} borderRadius="full" />
      <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mt={1}>
        {label}
      </Text>
      <Flex align="baseline" gap={2} mt={2} flexWrap="wrap" minW={0}>
        {/* The card can be as narrow as 150px and clips its overflow, so a long
            figure (e.g. "734,533,664" kWh) would be cut off at 28px. Step the
            size down as the string grows instead of truncating the number. */}
        <Text
          fontSize={metricValueFontSize(missing ? "n/a" : value)}
          fontWeight="700"
          color={missing ? "gray.500" : "darkTeal"}
          lineHeight="1"
          minW={0}
        >
          {missing ? "n/a" : value}
        </Text>
        {!missing && unit && (
          <Text fontSize="13px" color="gray.500" fontWeight="600">{unit}</Text>
        )}
        {!missing && pct != null && (
          <Tooltip
            label={`${priorLabel}: ${priorDisplay}`}
            hasArrow
            placement="top"
          >
            <Box as="span" cursor="help">
              <ChangeBadge pct={pct} lowerIsBetter={lowerIsBetter} />
            </Box>
          </Tooltip>
        )}
      </Flex>
      {sublabel && (
        <Text fontSize="11px" color="gray.500" mt={1}>{sublabel}</Text>
      )}
    </Box>
  );
};

const ChangeBadge = ({ pct, lowerIsBetter = true }) => {
  if (pct == null || !Number.isFinite(Number(pct))) return null;
  const isImproved = lowerIsBetter ? pct < 0 : pct > 0;
  const color = pct === 0 ? "gray.500" : isImproved ? "green.700" : "red.600";
  const bg = pct === 0 ? "gray.100" : isImproved ? "green.50" : "red.50";
  const Arrow = pct < 0 ? ArrowDown : ArrowUp;
  return (
    <HStack spacing={1} bg={bg} color={color} px={2} py={0.5} borderRadius="full" alignSelf="center">
      <Arrow size={10} weight="bold" />
      <Text fontSize="11px" fontWeight="600">{Math.abs(pct).toFixed(1)}%</Text>
    </HStack>
  );
};

// Renders a ChangeBadge wrapped in a tooltip showing the prior-year value
// (e.g. "Prior year: 4,100,000 kWh"). Returns null when there's no
// meaningful prior to compare against.
const YoyPill = ({ currentNumeric, priorNumeric, unit, digits = 0, lowerIsBetter = true, label = "Prior year" }) => {
  const pct = yoyPct(currentNumeric, priorNumeric);
  if (pct == null) return null;
  const isPct = unit === "%";
  const display = isPct
    ? fmtPct(priorNumeric, digits === 0 ? 1 : digits)
    : `${fmtNum(priorNumeric, digits)}${unit ? ` ${unit}` : ""}`;
  return (
    <Tooltip label={`${label}: ${display}`} hasArrow placement="top">
      <Box as="span" cursor="help">
        <ChangeBadge pct={pct} lowerIsBetter={lowerIsBetter} />
      </Box>
    </Tooltip>
  );
};

// Canonical primary KPI tile — matches the look used on ScopeOneDetail
// and the other Insights drill-down pages: small title + subtitle row,
// a fluid-clamped big number, and a chip+unit cluster pinned to the
// number's right side. Use only for the top-row tiles on each section
// where the dashboard wants visual parity with the rest of Insights.
const PrimaryKpiCard = ({
  accent,
  title,
  subtitle,
  value,
  unit = "kWh",
  currentNumeric,
  priorNumeric,
  priorDigits = 0,
  lowerIsBetter = true,
  loading,
  missing,
  footerLeading,
  footerValue,
  footerUnit,
}) => {
  const display = missing ? "n/a" : (loading ? "…" : (value ?? "—"));
  const pct = yoyPct(currentNumeric, priorNumeric);
  return (
    <Box position="relative" pt={3} minW={0} overflow="hidden" opacity={missing ? 0.7 : 1}>
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={missing ? "gray.300" : (accent || "gray.300")} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        {subtitle && <Text fontSize="13px" color="gray.500" noOfLines={1}>{subtitle}</Text>}
      </HStack>
      <Box
        position="relative"
        display="inline-block"
        whiteSpace="nowrap"
        sx={{ "--big": "clamp(20px, 2.6vw, 48px)" }}
      >
        <Flex align="flex-end" gap={3}>
          <Text
            fontSize="var(--big)"
            fontWeight="700"
            color={missing ? "gray.500" : "darkTeal"}
            lineHeight="1"
            letterSpacing="-0.02em"
          >
            {display}
          </Text>
          <Flex direction="column" justify="space-between" alignSelf="stretch" flexShrink={0}>
            {!missing && pct != null ? (
              <YoyPill
                currentNumeric={currentNumeric}
                priorNumeric={priorNumeric}
                unit={unit}
                digits={priorDigits}
                lowerIsBetter={lowerIsBetter}
              />
            ) : <Box />}
            <Text fontSize="calc(var(--big) * 0.36)" fontWeight="600" color="gray.600" lineHeight="1">
              {unit}
            </Text>
          </Flex>
        </Flex>
      </Box>
      {(footerValue || footerLeading) && (
        <HStack spacing={2} mt={3} align="baseline" flexWrap="wrap">
          {footerLeading && <Text fontSize="13px" color="gray.500">{footerLeading}</Text>}
          {footerValue && (
            <Text fontSize="20px" fontWeight="700" color="darkTeal" lineHeight="1">{footerValue}</Text>
          )}
          {footerUnit && <Text fontSize="13px" color="gray.500">{footerUnit}</Text>}
        </HStack>
      )}
    </Box>
  );
};

// Multi-year line chart styled like the ScopeThree TrendChart. Annual data
// points only — `/insights/total` is keyed by year, no quarter parameter.
// Quarterly resolution would need a backend addition; we surface that as
// a footnote rather than fabricating quarter splits.
const TrendLineChart = ({ series, colors, hint, unit = "kWh" }) => {
  if (!series || series.length === 0) return null;
  // Converted once here (not by the caller) so the axis scale, gridlines and
  // tooltip all read in the selected unit consistently.
  const divisor = UNIT_DIVISOR[unit] || 1;
  const displaySeries = series.map((s) => ({
    ...s,
    data: (s.data || []).map((d) => ({ ...d, y: (Number(d.y) || 0) / divisor })),
  }));
  // Fit the y-axis to the data range so small year-on-year variation is
  // visible. We pad the top by 8% of the range (or 10% of the max when the
  // range is zero) so the line never sits on the chart border. yMin stays
  // at 0 so the area fill never bleeds below the x axis.
  const allYs = displaySeries.flatMap((s) => (s.data || []).map((d) => Number(d.y) || 0));
  const dataMax = allYs.length ? Math.max(...allYs) : 0;
  const dataMin = allYs.length ? Math.min(...allYs) : 0;
  const range = dataMax - dataMin;
  const pad = range > 0 ? range * 0.08 : Math.max(dataMax * 0.10, 1);
  const yMax = dataMax + pad;
  const palette = colors || displaySeries.map((_, i) => ["#475569", "#60A5FA", "#0E7C66", "#34D399"][i % 4]);
  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="320px">
        <ResponsiveLine
          data={displaySeries}
          margin={{ top: 12, right: 24, bottom: 36, left: 72 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: yMax, stacked: false }}
          colors={palette}
          curve="monotoneX"
          enableArea
          areaOpacity={0.12}
          areaBaselineValue={0}
          enablePoints
          pointSize={6}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          pointColor="#FFFFFF"
          axisBottom={{ tickSize: 0, tickPadding: 6 }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            tickValues: 4,
            format: (v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v,
          }}
          gridYValues={4}
          enableGridX={false}
          theme={chartTheme}
          tooltip={({ point }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{point.data.x}</Text>
              <Text color={point.serieColor}>{point.serieId}: {fmtNum(point.data.y, unit === "MWh" ? 2 : 0)} {unit}</Text>
            </Box>
          )}
          useMesh
        />
      </Box>
      {hint && <Text fontSize="11px" color="gray.500" mt={2}>{hint}</Text>}
    </Box>
  );
};

// Concentric rings chart — one ring per category, ordered largest-on-the-
// outside. Uses Nivo's RadialBar with a single shared "year" group and a
// per-category slice. Each row carries `id`, `value` and `color`. The
// rings always render full size relative to the dataset max so smaller
// rings are still visible.
const ConcentricRingChart = ({ rows, height = 320, formatValue, bare = false, stretch = false }) => {
  // When stretch is true, fill the parent's height (parent must set h
  // explicitly — usually via a Grid row's stretched cell). Otherwise
  // stick with the fixed `height` so Nivo doesn't feedback-loop.
  const heightStyle = stretch ? "100%" : `${height}px`;
  if (!rows || rows.length === 0) {
    const emptyInner = (
      <Flex h="100%" align="center" justify="center"><Text color="gray.400" fontSize="13px">No data</Text></Flex>
    );
    return bare ? (
      <Box h={heightStyle} minH={`${height}px`}>{emptyInner}</Box>
    ) : (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={heightStyle} minH={`${height}px`}>
        {emptyInner}
      </Box>
    );
  }
  const sorted = [...rows].filter((r) => Number(r.value) > 0).sort((a, b) => b.value - a.value);
  // Each "group" is a separate concentric ring. Nivo expects the shape
  // `[{ id: groupName, data: [{ x: category, y: value }] }]`. By giving
  // each category its own group with one slice, we get N concentric
  // rings; the slice's x value drives the colour key.
  const data = sorted.map((r) => ({
    id: r.id,
    data: [{ x: r.id, y: Number(r.value) }],
  }));
  const colorById = Object.fromEntries(sorted.map((r) => [r.id, r.color || "#94A3B8"]));
  const total = sorted.reduce((acc, r) => acc + r.value, 0);
  // Fixed height keeps Nivo's ResponsiveRadialBar from feedback-looping
  // its parent's height — without it, h="100%" inside a flex column
  // makes the chart grow to fit and the parent grow to fit the chart.
  const wrapperProps = bare
    ? { h: heightStyle, minH: `${height}px` }
    : { bg: "white", border: "1px solid", borderColor: "gray.200", borderRadius: "md", p: 4, h: heightStyle, minH: `${height}px` };
  return (
    <Box {...wrapperProps}>
      <Box h="100%">
      <ResponsiveRadialBar
        data={data}
        margin={{ top: 16, right: 100, bottom: 16, left: 8 }}
        valueFormat={(v) => (formatValue ? formatValue(v) : fmtNum(v))}
        startAngle={90}
        endAngle={360}
        innerRadius={0.25}
        padding={0.3}
        cornerRadius={6}
        colors={({ category }) => colorById[category] || "#94A3B8"}
        borderWidth={0}
        enableTracks
        tracksColor="#F1F5F9"
        enableLabels={false}
        enableRadialGrid={false}
        enableCircularGrid={false}
        radialAxisStart={null}
        radialAxisEnd={{
          tickSize: 0,
          tickPadding: 8,
          tickRotation: 0,
        }}
        circularAxisOuter={null}
        theme={chartTheme}
        tooltip={({ bar }) => {
          const pct = total > 0 ? (bar.value / total) * 100 : 0;
          return (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{bar.category}</Text>
              <Text color="gray.600">
                {formatValue ? formatValue(bar.value) : fmtNum(bar.value)} · {pct.toFixed(1)}%
              </Text>
            </Box>
          );
        }}
      />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Real-data derivations from the /insights/total + /reports payloads.
// ---------------------------------------------------------------------------

// Carrier classification — CARRIER_GAS / CARRIER_LIQUID / CARRIER_OTHER.
// IMPORTANT: `row.consumptionType` from the API is the backend's DB DISPLAY
// NAME (InsightsFacade.extractScopeOneConsumptionData sends
// `consumption.getConsumptionType().getName()`, not the enum `code` column) —
// e.g. "Natural Gas", "Propane Gas (LPG)", "Bio-LNG", "Red Diesel (Gas Oil)".
// FUEL_CARRIER_MAP below is keyed by those exact display names (uppercased),
// confirmed against the full backend consumption_type table, all
// environments — the same 27 fuels are usable under either Stationary or
// Mobile (the group split is per-questionnaire-row, not per fuel), so carrier
// is classified once, independent of which group a row was entered under.
// RED_DIESEL/GAS_OIL are the same real fuel under two backend codes/names
// ("Red Diesel (Gas Oil)"/"Gas Oil"), merged into one "Gas oil" bucket;
// PROPANE_GAS/LPG ("Propane Gas (LPG)"/"LPG") likewise merge into one "LPG"
// bucket. (An earlier version of this map was keyed by the enum CODE instead
// of the display name — only 6 of 27 fuels have a name identical to their
// code, so every other fuel silently fell through to Other; fixed here.)
const CARRIER_GAS = "gas";
const CARRIER_LIQUID = "liquidFuels";
const CARRIER_OTHER = "other";

// Spec-named buckets per carrier, in display order. CNG / Marine / Solid
// fuels / Purchased heat / Purchased steam / Purchased cooling have NO
// backing consumption_type anywhere in the backend today (confirmed) — they
// still render (Empty States) but will always read 0 kWh until the backend
// adds a corresponding fuel code; that's a data-model gap, not a bug here.
const GAS_BUCKETS = ["Natural gas", "LNG", "CNG", "LPG"];
const LIQUID_FUEL_BUCKETS = ["Petrol", "Diesel", "HVO", "Biodiesel", "Gas oil", "Heating oil / kerosene", "Aviation", "Marine"];
const OTHER_BUCKETS = ["Biomass", "Solid fuels", "Purchased heat", "Purchased steam", "Purchased cooling"];
// ACETYLENE doesn't fit any spec-named bucket (not natural gas/LNG/LPG, not a
// liquid fuel, not biomass) — given its own bucket under Other rather than
// mis-filed into one it doesn't belong to.
const OTHER_EXTRA_BUCKETS = ["Other gas (Acetylene)"];

const FUEL_CARRIER_MAP = {
  "NATURAL GAS":                       { carrier: CARRIER_GAS,    bucket: "Natural gas" },
  "NATURAL GAS (100% MINERAL BLEND)":  { carrier: CARRIER_GAS,    bucket: "Natural gas" },
  "LNG":                               { carrier: CARRIER_GAS,    bucket: "LNG" },
  "BIO-LNG":                           { carrier: CARRIER_GAS,    bucket: "LNG" },
  "BIOMETHANE (LIQUIFIED)":            { carrier: CARRIER_GAS,    bucket: "LNG" },
  "PROPANE GAS (LPG)":                 { carrier: CARRIER_GAS,    bucket: "LPG" },
  "LPG":                               { carrier: CARRIER_GAS,    bucket: "LPG" },
  "PETROL":                            { carrier: CARRIER_LIQUID, bucket: "Petrol" },
  "PETROL (100% MINERAL)":             { carrier: CARRIER_LIQUID, bucket: "Petrol" },
  "PETROL (AVERAGE BIOFUEL BLEND)":    { carrier: CARRIER_LIQUID, bucket: "Petrol" },
  "PETROL SMALL CAR":                  { carrier: CARRIER_LIQUID, bucket: "Petrol" },
  "PETROL MEDIUM CAR":                 { carrier: CARRIER_LIQUID, bucket: "Petrol" },
  "PETROL LARGE CAR":                  { carrier: CARRIER_LIQUID, bucket: "Petrol" },
  "DIESEL":                            { carrier: CARRIER_LIQUID, bucket: "Diesel" },
  "DIESEL SMALL CAR":                  { carrier: CARRIER_LIQUID, bucket: "Diesel" },
  "DIESEL MEDIUM CAR":                 { carrier: CARRIER_LIQUID, bucket: "Diesel" },
  "DIESEL LARGE CAR":                  { carrier: CARRIER_LIQUID, bucket: "Diesel" },
  // Not in the confirmed backend fuel list I originally researched — added
  // after showing up as an unmapped "Other" row in real data. Own bucket
  // (parallels HVO) rather than merged into Diesel. Normalized matching
  // (below) covers "Biodiesel"/"Bio Diesel"/"Bio-Diesel"/"BIO_DIESEL" etc.
  // from this one entry — the exact DB string wasn't independently confirmed,
  // and guessing exact punctuation variants one at a time is exactly how
  // this fell through the first time.
  "BIODIESEL":                         { carrier: CARRIER_LIQUID, bucket: "Biodiesel" },
  "HVO":                               { carrier: CARRIER_LIQUID, bucket: "HVO" },
  "RED DIESEL (GAS OIL)":              { carrier: CARRIER_LIQUID, bucket: "Gas oil" },
  "GAS OIL":                           { carrier: CARRIER_LIQUID, bucket: "Gas oil" },
  "KEROSENE (BURNING OIL)":            { carrier: CARRIER_LIQUID, bucket: "Heating oil / kerosene" },
  "AVIATION SPIRIT":                   { carrier: CARRIER_LIQUID, bucket: "Aviation" },
  "AVIATION TURBINE FUEL":             { carrier: CARRIER_LIQUID, bucket: "Aviation" },
  "BIO MASS":                          { carrier: CARRIER_OTHER,  bucket: "Biomass" },
  "BIO MASS WOOD PELLETS":             { carrier: CARRIER_OTHER,  bucket: "Biomass" },
  "BIO MASS WOOD LOGS":                { carrier: CARRIER_OTHER,  bucket: "Biomass" },
  "ACETYLENE":                         { carrier: CARRIER_OTHER,  bucket: "Other gas (Acetylene)" },
};

// Strips everything but letters/digits before matching, so "Bio Diesel",
// "Bio-Diesel", "BIO_DIESEL" and "Biodiesel" all resolve identically — exact-
// string matching on the raw display name was too fragile (that's exactly
// how Biodiesel fell through to Other despite being "in the map": the guess
// covered the wrong punctuation variant). Built once from FUEL_CARRIER_MAP.
const normalizeFuelName = (name) => String(name || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const FUEL_CARRIER_LOOKUP = new Map(
  Object.entries(FUEL_CARRIER_MAP).map(([key, value]) => [normalizeFuelName(key), value])
);

// Orgs can create BESPOKE/custom fuel types with an arbitrary name (Settings
// → Conversion Factors) — e.g. a real one seen in production: "Biodiesel me".
// No fixed map, however normalized, can be exhaustive against an org-chosen
// name. After an exact normalized match fails, fall back to keyword
// substrings — deliberately only the unambiguous ones. No bare "GAS" keyword
// here: that's exactly the substring that mis-filed GAS_OIL as Gas in the
// original bug, and a bespoke name mentioning "gas" is genuinely ambiguous
// (could be Gas oil, a liquid). Falling through to Other-with-its-real-name
// is a safe, honest default; a wrong carrier is not.
const FUEL_KEYWORD_FALLBACKS = [
  { test: /BIODIESEL/, carrier: CARRIER_LIQUID, bucket: "Biodiesel" },
  { test: /HVO/,       carrier: CARRIER_LIQUID, bucket: "HVO" },
  { test: /KEROSENE/,  carrier: CARRIER_LIQUID, bucket: "Heating oil / kerosene" },
  { test: /AVIATION/,  carrier: CARRIER_LIQUID, bucket: "Aviation" },
  { test: /MARINE/,    carrier: CARRIER_LIQUID, bucket: "Marine" },
  { test: /DIESEL/,    carrier: CARRIER_LIQUID, bucket: "Diesel" },
  { test: /PETROL/,    carrier: CARRIER_LIQUID, bucket: "Petrol" },
  { test: /LNG/,       carrier: CARRIER_GAS,    bucket: "LNG" },
  { test: /LPG|PROPANE/, carrier: CARRIER_GAS,  bucket: "LPG" },
  { test: /CNG/,        carrier: CARRIER_GAS,   bucket: "CNG" },
  { test: /BIOMASS/,    carrier: CARRIER_OTHER, bucket: "Biomass" },
  { test: /ACETYLENE/,  carrier: CARRIER_OTHER, bucket: "Other gas (Acetylene)" },
];

const resolveFuel = (name) => {
  const normalized = normalizeFuelName(name);
  const exact = FUEL_CARRIER_LOOKUP.get(normalized);
  if (exact) return exact;
  const fallback = FUEL_KEYWORD_FALLBACKS.find((f) => f.test.test(normalized));
  return fallback ? { carrier: fallback.carrier, bucket: fallback.bucket } : null;
};

// A genuinely unrecognised name (no exact match, no keyword match) defaults
// to Other rather than being silently dropped from the Total — still shown
// under its own real name (see buildCarrierBreakdown's unmapped fallback).
const fuelCarrierOf = (name) => resolveFuel(name)?.carrier || CARRIER_OTHER;
const fuelBucketOf = (name) => resolveFuel(name)?.bucket || prettyFuelLabel(name);

// EV charging — electricity rows for owned-fleet EVs (ELECTRIC_CAR* codes).
// Counted in BOTH the Electricity carrier (it's purchased electricity) and
// the Transport usage view (it's what moves the vehicles) — usage
// legitimately cuts across carriers (see Section 3 / Usage breakdowns).
const evChargingKwh = (insights) =>
  (insights?.scopeOneAndTwoDetail?.electricity || [])
    .filter((r) => /^ELECTRIC_CAR/.test(String(r.consumptionType || "").toUpperCase()))
    .reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);

const buildEnergyTotals = (insights, greyFleetKwh = 0) => {
  if (!insights) return null;
  const detail = insights.scopeOneAndTwoDetail;
  if (!detail) return null;
  const stationaryRows = detail.stationary || [];
  const mobileRows     = detail.mobile     || [];
  // Carrier is a fuel-identity question, not a use question — a code like
  // DIESEL counts toward the Liquid fuels carrier whether it was entered
  // under Stationary (a generator) or Mobile (a van); the backend doesn't
  // even split the two at the consumption_type level (see FUEL_CARRIER_MAP).
  const fuelRows = [...stationaryRows, ...mobileRows];

  const evKwh = evChargingKwh(insights);
  // Electricity total: v2 orgs sum every purchased-electricity category
  // (each a distinct physical purchase) plus EV charging. v1 orgs use
  // Standard Grid (location-based) ONLY — Renewable Energy Procurement,
  // Onsite Generation and EV are real categories still shown in the
  // Electricity breakdown panel for detail, but are deliberately excluded
  // from this headline total for v1, so the breakdown intentionally does
  // NOT sum to this card for v1 orgs (accepted exception, like the Usage
  // sections in Section 3).
  const isV2Electricity = v2PurchasedElectricity(insights).length > 0;
  const elecKwh = isV2Electricity ? electricityKwh(insights) + evKwh : standardGridKwh(insights);

  const gasFuelRows = fuelRows.filter((r) => fuelCarrierOf(r.consumptionType) === CARRIER_GAS);
  const liquidFuelRows = fuelRows.filter((r) => fuelCarrierOf(r.consumptionType) === CARRIER_LIQUID);
  const otherFuelRows = fuelRows.filter((r) => fuelCarrierOf(r.consumptionType) === CARRIER_OTHER);
  const gasKwh = gasFuelRows.reduce((acc, r) => acc + rowKwh(r), 0);
  const liquidFuelsKwh = liquidFuelRows.reduce((acc, r) => acc + rowKwh(r), 0);
  const otherKwh = otherFuelRows.reduce((acc, r) => acc + rowKwh(r), 0);

  // Usage view (Section 3) — cuts across carriers by design, excluded from
  // totalKwh below. Transport = owned-fleet mobile + EV mileage + grey-fleet
  // (Cat 6 Business Travel Cars; SECR scope even though the underlying
  // emissions are reported as Scope 3). Stationary = every Scope 1 Stationary
  // row regardless of carrier (boilers/generators/process plant/buildings).
  const ownedFleetKwh = mobileRows.reduce((acc, r) => acc + rowKwh(r), 0);
  const transportKwh = ownedFleetKwh + evKwh + (Number(greyFleetKwh) || 0);
  const stationaryUsageKwh = stationaryRows.reduce((acc, r) => acc + rowKwh(r), 0);

  // Flag when any fuel row has consumption but no calorific factor, so the UI
  // can note that its energy is excluded rather than looking mistakenly low.
  const kwhMissing = fuelRows.some(isKwhMissing);

  return {
    elecKwh,
    gasKwh,
    liquidFuelsKwh,
    otherKwh,
    totalKwh: elecKwh + gasKwh + liquidFuelsKwh + otherKwh,
    transportKwh,
    stationaryUsageKwh,
    kwhMissing,
  };
};

// Scope 2 v2 purchased-electricity category → the four purchase-category labels
// used by the breakdown donut/legend (matching ELEC_PALETTE). ONSITE is the
// self-generated summary row and is intentionally excluded — it is disclosed in
// its own Onsite generation section, not part of purchased electricity.
const V2_CATEGORY_TO_LABEL = {
  STANDARD_GRID:       "Standard Grid",
  SUPPLIER_CONTRACTED: "Supplier Contracted",
  CERTIFIED_RENEWABLE: "Certified Renewable",
  DIRECT_PRIVATE_WIRE: "Direct / Private Wire",
};

// Non-ONSITE v2 electricity rows for the period, or [] when the org is on v1
// (no v2 rows). `consumptionKwh` is the real per-category kWh.
const v2PurchasedElectricity = (insights) =>
  (insights?.electricityV2 || []).filter((c) => c && c.category !== "ONSITE");

// Total purchased-electricity kWh. Prefers the Scope 2 v2 per-category kWh when
// the org is on v2 (each category is a distinct physical purchase, so they sum);
// falls back to the v1 electricity rows, where LOCATION and MARKET are the same
// physical kWh so MARKET is excluded to avoid double-counting.
const electricityKwh = (insights) => {
  const v2 = v2PurchasedElectricity(insights);
  if (v2.length > 0) {
    return v2.reduce((acc, c) => acc + (Number(c.consumptionKwh) || 0), 0);
  }
  const rows = insights?.scopeOneAndTwoDetail?.electricity || [];
  return rows
    .filter((r) => String(r.consumptionType || "").toUpperCase() !== "ELECTRICITY_MARKET_BASED")
    .reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);
};

// v1-only: Standard Grid (location-based) consumption alone — used for the
// Electricity/Total headline cards on v1 orgs (see buildEnergyTotals).
const standardGridKwh = (insights) => {
  const rows = insights?.scopeOneAndTwoDetail?.electricity || [];
  return rows
    .filter((r) => String(r.consumptionType || "").toUpperCase() === "ELECTRICITY_LOCATION_BASED")
    .reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);
};

const buildElectricityBreakdown = (insights) => {
  const buckets = {
    "Standard Grid":         0,
    "Supplier Contracted":   0,
    "Certified Renewable":   0,
    "Direct / Private Wire": 0,
    "EV charging":           0,
  };
  // Prefer the Scope 2 v2 per-category breakdown when present. A v2 org has NO
  // rows in `scopeOneAndTwoDetail.electricity` (that list is mapped only from the
  // v1 ReportElectricityItem table), so the v1 branch below would yield nothing.
  const v2 = v2PurchasedElectricity(insights);
  if (v2.length > 0) {
    v2.forEach((c) => {
      const label = V2_CATEGORY_TO_LABEL[c.category];
      if (label) buckets[label] += Number(c.consumptionKwh) || 0;
    });
  } else {
    // v1: ELECTRICITY_MARKET_BASED is never a real entered row — it's always
    // derived (ReportMarketBasedElectricityCalculationService synthesizes it
    // from location-based minus renewable, whenever any location-based
    // electricity exists), and the card total already excludes it to avoid
    // double-counting the same physical kWh. So "Supplier Contracted" has no
    // real-data meaning for v1 and is deliberately left out here — v2's
    // SUPPLIER_CONTRACTED (above) is a genuine entered category, kept.
    const rows = insights?.scopeOneAndTwoDetail?.electricity || [];
    rows.forEach((r) => {
      const t = String(r.consumptionType || "").toUpperCase();
      const v = Number(r.consumption) || 0;
      if (t === "ELECTRICITY_LOCATION_BASED")    buckets["Standard Grid"]         += v;
      else if (t === "RENEWABLE_ENERGY_PROCUREMENT") buckets["Certified Renewable"] += v;
      else if (t === "ELECTRICITY_ONSITE_GENERATION") buckets["Direct / Private Wire"] += v;
    });
  }
  // EV charging on company meters/charge cards — now part of the Electricity
  // carrier total (see buildEnergyTotals), so it needs its own slice here too
  // or the breakdown wouldn't sum to the card.
  buckets["EV charging"] = evChargingKwh(insights);
  // Drop empty buckets so the donut/list isn't cluttered with zero categories.
  return Object.entries(buckets)
    .filter(([, v]) => v > 0)
    .map(([id, value]) => ({ id, value }));
};

// Normalises a display name/code that isn't in FUEL_CARRIER_MAP (none exist
// today; defensive for a future backend addition) — e.g. an enum-style
// leftover like GAS_100_MINERAL would render as "Gas 100 mineral".
const prettyFuelLabel = (value) => {
  if (typeof value !== "string" || !value) return value;
  const lower = value.replace(/[_-]+/g, " ").trim().toLowerCase();
  return lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : value;
};

// Shared fixed-bucket builder for the carrier breakdowns (Gas / Liquid fuels /
// Other). Named buckets with no matching data (including the CNG/Marine/
// Solid-fuels/Purchased-heat-steam-cooling gap categories with no backing
// data at all) are dropped rather than shown at zero. `fuelRows` is the
// combined stationary+mobile set (carrier is use-agnostic, see
// buildEnergyTotals); filtered to `carrier` here.
const buildCarrierBreakdown = (insights, carrier, bucketList, extraBuckets = []) => {
  const stationaryRows = insights?.scopeOneAndTwoDetail?.stationary || [];
  const mobileRows = insights?.scopeOneAndTwoDetail?.mobile || [];
  const allBuckets = [...bucketList, ...extraBuckets];
  const kwhByBucket = new Map(allBuckets.map((b) => [b, 0]));
  // A row can be classified into this carrier (fuelCarrierOf defaults any
  // unrecognised name to Other) while its bucket name doesn't match one of
  // the fixed named buckets above (fuelBucketOf's fallback is the raw
  // pretty-printed name, not one of them) — e.g. a real consumption_type
  // whose exact display-name string isn't yet in FUEL_CARRIER_MAP. Without
  // this, such a row would still count toward the carrier's card total but
  // vanish from its own breakdown, breaking the "breakdown sums to card"
  // guarantee silently. Give it its own line under its real name instead.
  const unmappedByName = new Map();
  [...stationaryRows, ...mobileRows]
    .filter((r) => fuelCarrierOf(r.consumptionType) === carrier)
    .forEach((r) => {
      const bucket = fuelBucketOf(r.consumptionType);
      const kwh = rowKwh(r);
      if (kwhByBucket.has(bucket)) {
        kwhByBucket.set(bucket, kwhByBucket.get(bucket) + kwh);
      } else {
        unmappedByName.set(bucket, (unmappedByName.get(bucket) || 0) + kwh);
      }
    });
  // Sorted desc so the largest bucket leads (matches the "top fuel" metric
  // card below); zero buckets dropped.
  return [
    ...allBuckets.map((id) => ({ id, value: kwhByBucket.get(id) || 0 })),
    ...Array.from(unmappedByName, ([id, value]) => ({ id, value })),
  ]
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value);
};

const buildGasBreakdown = (insights) => buildCarrierBreakdown(insights, CARRIER_GAS, GAS_BUCKETS);
const buildLiquidFuelsBreakdown = (insights) => buildCarrierBreakdown(insights, CARRIER_LIQUID, LIQUID_FUEL_BUCKETS);
const buildOtherCarrierBreakdown = (insights) => buildCarrierBreakdown(insights, CARRIER_OTHER, OTHER_BUCKETS, OTHER_EXTRA_BUCKETS);

const buildFuelBreakdown = (insights, greyFleetKwh, greyFleetStats = { rows: 0, tCO2e: 0 }) => {
  const mobile = insights?.scopeOneAndTwoDetail?.mobile || [];
  const electricity = insights?.scopeOneAndTwoDetail?.electricity || [];
  const buckets = {
    Diesel: 0,
    Petrol: 0,
    HVO: 0,
    "Other mobile fuel": 0,
    EV: 0,
    "Grey fleet": Number(greyFleetKwh) || 0,
  };
  // Track raw source rows + their pre-conversion totals so the tooltip
  // can show "Diesel · 3 rows · 8,420 L → 92,283 kWh" — makes it
  // unambiguous when a fuel reads 0 kWh whether the source has nothing
  // or the conversion silently dropped rows. "Other mobile fuel" mixes
  // measures (LPG in kg, others in L) across rows, so its raw quantity
  // isn't meaningful to sum — rawUnit left blank hides that line in the
  // tooltip. It carries `byFuel` instead — a per-real-fuel-name kWh split
  // (LPG, Natural Gas, Kerosene, Aviation Spirit, etc. can all land here,
  // any mobile row that isn't Diesel/Petrol/HVO) — so the tooltip can show
  // exactly what's inside the bucket rather than leaving it opaque.
  const debug = {
    Diesel: { rows: 0, raw: 0, rawUnit: "L" },
    Petrol: { rows: 0, raw: 0, rawUnit: "L" },
    HVO:    { rows: 0, raw: 0, rawUnit: "L" },
    "Other mobile fuel": { rows: 0, raw: 0, rawUnit: "", byFuel: {} },
    EV:     { rows: 0, raw: 0, rawUnit: "kWh" },
    "Grey fleet": { rows: greyFleetStats.rows, raw: greyFleetStats.tCO2e, rawUnit: "tCO₂e" },
  };
  mobile.forEach((r) => {
    const code = String(r.consumptionType || "").toUpperCase();
    const kwh = rowKwh(r);
    const raw = Number(r.consumption) || 0;
    if (code.includes("DIESEL")) {
      buckets.Diesel += kwh;
      debug.Diesel.rows += 1;
      debug.Diesel.raw += raw;
    } else if (code.includes("PETROL")) {
      buckets.Petrol += kwh;
      debug.Petrol.rows += 1;
      debug.Petrol.raw += raw;
    } else if (code === "HVO") {
      buckets.HVO += kwh;
      debug.HVO.rows += 1;
      debug.HVO.raw += raw;
    } else {
      // Any other mobile fuel — e.g. LPG/autogas vehicles, or a company
      // aircraft's Kerosene/Aviation fuel entered under Mobile — previously
      // silently excluded from this breakdown while still counting toward
      // the Transport card total (buildEnergyTotals sums every mobile row
      // unconditionally), so it looked "lost."
      buckets["Other mobile fuel"] += kwh;
      debug["Other mobile fuel"].rows += 1;
      debug["Other mobile fuel"].raw += raw;
      const fuelName = prettyFuelLabel(r.consumptionType) || "Unknown";
      debug["Other mobile fuel"].byFuel[fuelName] = (debug["Other mobile fuel"].byFuel[fuelName] || 0) + kwh;
    }
  });
  electricity.forEach((r) => {
    if (/^ELECTRIC_CAR/.test(String(r.consumptionType || "").toUpperCase())) {
      const kwh = Number(r.consumption) || 0;
      buckets.EV += kwh;
      debug.EV.rows += 1;
      debug.EV.raw += kwh;
    }
  });
  return Object.entries(buckets).map(([id, value]) => ({ id, value, debug: debug[id] }));
};

// Every Scope 1 Stationary row, regardless of carrier (boilers, generators,
// process plant) — mirrors Transport's shape above (one row per fuel actually
// present, sorted desc) rather than Section 2's fixed-bucket treatment:
// Stationary spans all ~27 possible fuel codes, so an always-full bucket list
// would be unwieldy for one section. Reuses fuelBucketOf() so the RED_DIESEL/
// GAS_OIL and PROPANE_GAS/LPG merges stay consistent with the carrier
// breakdowns above.
const buildStationaryBreakdown = (insights) => {
  const rows = insights?.scopeOneAndTwoDetail?.stationary || [];
  const buckets = new Map();
  rows.forEach((r) => {
    const id = fuelBucketOf(r.consumptionType);
    buckets.set(id, (buckets.get(id) || 0) + rowKwh(r));
  });
  return Array.from(buckets.entries())
    .filter(([, value]) => value > 0)
    .map(([id, value]) => ({ id, value }))
    .sort((a, b) => b.value - a.value);
};

const buildWasteRoutes = (wasteItems) => {
  const buckets = {
    Recycled: 0,
    "Other recovery": 0,
    Incineration: 0,
    Landfill: 0,
  };
  (wasteItems || []).forEach((it) => {
    const dm = String(it.disposalMethod || "").toUpperCase();
    const tonnes = Number(it.weight) || 0;
    if (tonnes <= 0) return;
    if (dm === "RECYCLED") buckets.Recycled += tonnes;
    else if (dm === "COMBUSTION") buckets.Incineration += tonnes;
    else if (dm === "LANDFILL") buckets.Landfill += tonnes;
    else if (dm === "REUSE" || dm === "COMPOSTING" || dm === "ANAEROBIC_DIGESTION") {
      buckets["Other recovery"] += tonnes;
    } else {
      buckets["Other recovery"] += tonnes;
    }
  });
  return Object.entries(buckets).map(([id, value]) => ({ id, value }));
};

// Bucket waste tonnage by the high-level activity/family (Construction,
// Plastic, Paper, Organic, Refuse, Other) so the second ring chart shows
// "what kind of waste are we generating" alongside the disposal-route chart.
// Falls back to a "Other" bucket so any unmapped activity still contributes
// to the total rather than silently disappearing.
const WASTE_CATEGORY_LABELS = {
  CONSTRUCTION: "Construction",
  PLASTIC: "Plastic",
  PAPER: "Paper & card",
  ORGANIC: "Organic",
  REFUSE: "Refuse",
  ELECTRICAL: "Electrical",
  METAL: "Metal",
  GLASS: "Glass",
  HAZARDOUS: "Hazardous",
};

const buildWasteCategories = (wasteItems) => {
  const buckets = new Map();
  (wasteItems || []).forEach((it) => {
    const tonnes = Number(it.weight) || 0;
    if (tonnes <= 0) return;
    const code = String(it.wasteActivity || it.wasteType || "OTHER").toUpperCase();
    const label = WASTE_CATEGORY_LABELS[code] || "Other";
    buckets.set(label, (buckets.get(label) || 0) + tonnes);
  });
  return Array.from(buckets.entries()).map(([id, value]) => ({ id, value }));
};

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

// Top-row SECR KPI cards — must mirror the rest of Insights' big-number
// behaviour: number sizes fluidly so it's as large as possible without
// wrapping or overflowing. Do NOT swap this for static fontSize tokens.
//
// Unit ("kWh") is placed next to the title at the top of the card — matches
// the Total Emissions cards' "(tCO2e)" subtitle pattern in SummaryCards.jsx.
//
// Sizing uses CSS container queries (`cqi` = 1% of the container's inline
// size) so the number scales with the *card* width, not the viewport. A
// vw-based clamp falls down here because the card only gets 1/4 of the
// viewport at 4-up — a 9-digit value like "506,178,012" overflows whenever
// the clamp picks the upper bound for its viewport but the card is narrower.
// `containerType: "inline-size"` on the wrapper is what enables `cqi`.
const SecrTopKpiCard = ({ title, value, currentNumeric, priorNumeric, missing, unit = "kWh" }) => {
  const pct = yoyPct(currentNumeric, priorNumeric);
  return (
    <DashboardCard bg="#F7FAFC">
      <Text fontSize="body.2xl" fontWeight="bold" color="teal">
        {title}{" "}
        <Text as="span" fontSize="body.s" fontWeight="bold" color="gray.700">
          ({unit})
        </Text>
      </Text>
      <Box
        minW={0}
        sx={{
          containerType: "inline-size",
          "--big": "clamp(18px, 11cqi, 44px)",
        }}
      >
        <Flex align="center" gap={2} minW={0}>
          <Text
            fontSize="var(--big)"
            fontWeight="bold"
            color={missing ? "gray.500" : "gray.800"}
            lineHeight="1"
            letterSpacing="-0.02em"
            whiteSpace="nowrap"
          >
            {missing ? "N/A" : value}
          </Text>
          {!missing && pct != null && <YoYChip pctChange={pct} />}
        </Flex>
      </Box>
    </DashboardCard>
  );
};

// Page-wide master switch — kWh vs MWh for every energy figure on this page
// (cards, breakdowns, charts, tables, onsite generation). Same pill-button
// pattern as every other toggle in this file.
const UnitToggle = ({ unit, setUnit }) => (
  <Flex bg="gray.100" borderRadius="full" p="3px">
    {["kWh", "MWh"].map((option) => {
      const active = unit === option;
      return (
        <Button
          key={option}
          size="xs"
          borderRadius="full"
          bg={active ? "pureBlack" : "transparent"}
          color={active ? "white" : "gray.500"}
          fontWeight={active ? "600" : "normal"}
          onClick={() => setUnit(option)}
          _hover={{ bg: active ? "pureBlack" : "gray.200" }}
          px={3}
        >
          {option}
        </Button>
      );
    })}
  </Flex>
);

// Section 1 — Total cards. Chart/Table switch mirrors the local pattern used
// on every drill-down page (e.g. ScopeTwoChartViewToggle in ScopeTwoDetail.jsx)
// — there's no single shared toggle component in this codebase, each page
// hand-rolls the identical pill-button JSX.
const EnergyChartViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Chart", value: "chart", icon: <ChartLine size={14} /> },
    { label: "Table", value: "table", icon: <ListBullets size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Table view — carriers as rows, quarters as columns, reusing the one
// genuinely shared piece of this pattern (QuarterlyBreakdownTable, already
// used by ScopeOneDetail/ScopeTwoDetail/ScopeThreeDetail's own Table views).
// Each quarter's carrier totals are computed via buildEnergyTotals — the
// exact same function the cards above use — so this table can never drift
// from them.
const CarrierQuarterlyTable = ({ year, dimensionParams, unit }) => {
  const compare = useQuarterlyTableCompare();
  const currentQueries = useGetQuarterlyConsumption(year, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
  });
  const prevQueries = useGetQuarterlyConsumption(year - 1, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
    enabled: compare.needsPreviousYear,
  });
  const energyOf = (d) => buildEnergyTotals(d?.energy ? { scopeOneAndTwoDetail: d.energy } : null, d?.greyFleet?.derivedKwh || 0);
  const payloads = currentQueries.map((q) => (q.status === "success" ? energyOf(q.data) : null));
  const prevPayloads = prevQueries.map((q) => (q.status === "success" ? energyOf(q.data) : null));

  const ROWS = [
    { label: "Electricity",   get: (d) => d?.elecKwh },
    { label: "Gas",           get: (d) => d?.gasKwh },
    { label: "Liquid fuels",  get: (d) => d?.liquidFuelsKwh },
    { label: "Other",         get: (d) => d?.otherKwh },
    { label: "Total",         bold: true, get: (d) => d?.totalKwh },
  ];
  const rows = ROWS.map((row) => ({
    label: row.label,
    bold: row.bold,
    values: payloads.map((d) => toDisplayUnit(row.get(d), unit)),
    prevValues: prevPayloads.map((d) => toDisplayUnit(row.get(d), unit)),
  }));

  const isLoading =
    currentQueries.some((q) => q.isLoading) ||
    (compare.needsPreviousYear && prevQueries.some((q) => q.isLoading));

  return <QuarterlyBreakdownTable year={year} rows={rows} isLoading={isLoading} compare={compare} unitLabel={unit} />;
};

const EnergyConsumptionSection = ({
  current, prior, currentFy, hasData, kwhMissing, trendSeries, seriesConfig, visibleSeries, toggleSeries,
  view, setView, year, dimensionParams, unit, isLoading,
}) => {
  if (isLoading) {
    return (
      <Box>
        <SectionSkeleton variant="cards" />
        <SectionSkeleton variant="ring" />
      </Box>
    );
  }
  return (
  <Box>
    {!hasData && (
      <NotAvailableNote>
        No Scope 1/2 data has been entered for {currentFy}. Once questionnaires are finalised the headline kWh totals
        will populate from the same /insights/total feed that drives the Total Emissions tab.
      </NotAvailableNote>
    )}
    {hasData && kwhMissing && (
      <NotAvailableNote>
        Some fuel rows have no calorific value configured, so their kWh energy equivalent is excluded from the
        totals below (emissions are still counted elsewhere). Configure the fuel&apos;s calorific value in
        Settings → Conversion Factors to include it here.
      </NotAvailableNote>
    )}
    <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
      <SecrTopKpiCard
        title="Total"
        value={fmtEnergy(current?.totalKwh, unit)}
        currentNumeric={current?.totalKwh}
        priorNumeric={prior?.totalKwh}
        missing={!hasData}
        unit={unit}
      />
      <SecrTopKpiCard
        title="Electricity"
        value={fmtEnergy(current?.elecKwh, unit)}
        currentNumeric={current?.elecKwh}
        priorNumeric={prior?.elecKwh}
        missing={!hasData}
        unit={unit}
      />
      <SecrTopKpiCard
        title="Gas"
        value={fmtEnergy(current?.gasKwh, unit)}
        currentNumeric={current?.gasKwh}
        priorNumeric={prior?.gasKwh}
        missing={!hasData}
        unit={unit}
      />
      <SecrTopKpiCard
        title="Liquid fuels"
        value={fmtEnergy(current?.liquidFuelsKwh, unit)}
        currentNumeric={current?.liquidFuelsKwh}
        priorNumeric={prior?.liquidFuelsKwh}
        missing={!hasData}
        unit={unit}
      />
      {current?.otherKwh > 0 && (
        <SecrTopKpiCard
          title="Other"
          value={fmtEnergy(current?.otherKwh, unit)}
          currentNumeric={current?.otherKwh}
          priorNumeric={prior?.otherKwh}
          missing={!hasData}
          unit={unit}
        />
      )}
    </SimpleGrid>
    <Flex justify="flex-end" mb={2}>
      <EnergyChartViewToggle view={view} setView={setView} />
    </Flex>
    {view === "table" ? (
      <CarrierQuarterlyTable year={year} dimensionParams={dimensionParams} unit={unit} />
    ) : (
      trendSeries && trendSeries.length > 0 && (
        <Box>
          {seriesConfig && (
            <Flex justify="flex-end" mb={2}>
              <HStack spacing={2} flexWrap="wrap">
                {seriesConfig.map((s) => {
                  const active = visibleSeries.has(s.key);
                  return (
                    <Tag
                      key={s.key}
                      size="sm"
                      borderRadius="full"
                      cursor="pointer"
                      border="1px solid"
                      borderColor={active ? s.color : "gray.300"}
                      bg={active ? s.lightColor : "transparent"}
                      color={active ? s.textColor : "gray.500"}
                      fontWeight="600"
                      onClick={() => !active && toggleSeries(s.key)}
                      _hover={{ bg: active ? s.lightColor : "gray.50" }}
                      transition="all 0.15s"
                    >
                      <TagLabel>{s.label}</TagLabel>
                      {active && (
                        <TagCloseButton
                          onClick={(e) => { e.stopPropagation(); toggleSeries(s.key); }}
                        />
                      )}
                    </Tag>
                  );
                })}
              </HStack>
            </Flex>
          )}
          <TrendLineChart
            series={trendSeries.filter((s) => !visibleSeries || visibleSeries.has(s.id))}
            colors={trendSeries
              .filter((s) => !visibleSeries || visibleSeries.has(s.id))
              .map((s) => (seriesConfig?.find((c) => c.key === s.id)?.color) || SECR_PALETTE.emissionsLB)}
            hint="Annual total energy and per-source split across the last five reporting years. Quarterly points would require a per-quarter aggregation endpoint — /insights/total is keyed by year only."
            unit={unit}
          />
        </Box>
      )
    )}
  </Box>
  );
};

const ELEC_PALETTE = {
  "Standard Grid":         "#94A3B8",
  "Supplier Contracted":   "#60A5FA",
  "Certified Renewable":   "#34D399",
  "Direct / Private Wire": "#0E7C66",
  "EV charging":           "#0EA5E9",
};

const ElectricityBreakdownSection = ({
  rows, totalKwh, priorRows, priorTotalKwh, mixedV1V2, hiddenV1Kwh, headlineKwh, priorHeadlineKwh, unit, isLoading,
}) => {
  if (isLoading) {
    return (
      <Box>
        <SectionHeading title="Electricity breakdown" dataState="loading" />
        <SectionSkeleton variant="ring" />
      </Box>
    );
  }
  const renewableKwh = rows
    .filter((r) => r.id === "Certified Renewable" || r.id === "Direct / Private Wire")
    .reduce((acc, r) => acc + r.value, 0);
  const renewablePct = totalKwh > 0 ? (renewableKwh / totalKwh) * 100 : 0;
  const priorRenewableKwh = (priorRows || [])
    .filter((r) => r.id === "Certified Renewable" || r.id === "Direct / Private Wire")
    .reduce((acc, r) => acc + r.value, 0);
  const priorRenewablePct = (priorTotalKwh || 0) > 0 ? (priorRenewableKwh / priorTotalKwh) * 100 : null;
  const hasData = rows.length > 0 && totalKwh > 0;
  return (
    <Box>
      <SectionHeading
        title="Electricity breakdown"
        info="Electricity split by GHG Protocol Scope 2 purchase category. Renewable share = Certified Renewable + Direct / Private Wire. Supplier Contracted only shows for Scope 2 v2 organisations — on v1, it's a derived market-based figure rather than a real entered category, so it's left out here. For v1 organisations, the Electricity/Total cards above show Standard Grid only — Renewable/Onsite/EV are shown here for detail but aren't rolled into those headline totals, so this breakdown intentionally won't sum to the card for v1."
        dataState={hasData ? "real" : "missing"}
      />
      {mixedV1V2 && (
        <NotAvailableNote>
          This period mixes Scope 2 v2 and legacy (v1) electricity questionnaires. The breakdown and {unit} below reflect the
          v2 questionnaires only{hiddenV1Kwh > 0 ? ` — about ${fmtEnergy(hiddenV1Kwh, unit)} ${unit} of legacy electricity is not shown here` : ""}.
          Headline emission totals (Total Emissions tab) still include both.
        </NotAvailableNote>
      )}
      {!hasData && (
        <NotAvailableNote>
          No electricity has been recorded for the selected period — the breakdown populates from the electricity rows in
          the Scope 1/2 questionnaires.
        </NotAvailableNote>
      )}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
        <Box h="380px">
          <ConcentricRingChart
            rows={rows.map((r) => ({ ...r, color: ELEC_PALETTE[r.id] || "#94A3B8" }))}
            height={320}
            formatValue={(v) => `${fmtEnergy(v, unit)} ${unit}`}
            stretch
          />
        </Box>
        <Flex direction="column" gap={3} h="380px" minW={0}>
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} flex="1" overflow="auto">
            <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>Purchase categories</Text>
            <Flex direction="column" gap={2}>
              {rows.map((r) => {
                const pct = totalKwh > 0 ? (r.value / totalKwh) * 100 : 0;
                const priorVal = (priorRows || []).find((p) => p.id === r.id)?.value;
                return (
                  <Flex key={r.id} align="center" gap={2} minW={0}>
                    <Box w="10px" h="10px" borderRadius="sm" bg={ELEC_PALETTE[r.id] || "#94A3B8"} flexShrink={0} />
                    <Text flex="1" fontSize="13px" color="gray.700" minW={0} noOfLines={1}>{r.id}</Text>
                    <Text fontSize="13px" color="gray.700" fontWeight="600" whiteSpace="nowrap" flexShrink={0}>{fmtEnergy(r.value, unit)} {unit}</Text>
                    <Text fontSize="12px" color="gray.500" minW="44px" textAlign="right" flexShrink={0}>{hasData ? `${pct.toFixed(1)}%` : "—"}</Text>
                    <Box minW="56px" display="flex" justifyContent="flex-end" flexShrink={0}>
                      <YoyPill
                        currentNumeric={toDisplayUnit(r.value, unit)}
                        priorNumeric={toDisplayUnit(priorVal, unit)}
                        unit={unit}
                        digits={unit === "MWh" ? 2 : 0}
                        lowerIsBetter={r.id === "Standard Grid"}
                      />
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          </Box>
          <SimpleGrid minChildWidth="150px" spacing={3}>
            <MetricCard accent={SECR_PALETTE.electricity} label="Total" value={fmtEnergy(headlineKwh, unit)} unit={unit} sublabel="Sum of all electricity" missing={!hasData}
              currentNumeric={toDisplayUnit(headlineKwh, unit)} priorNumeric={toDisplayUnit(priorHeadlineKwh, unit)} priorDigits={unit === "MWh" ? 2 : 0} />
            <MetricCard accent="#34D399" label="Total renewable" value={fmtEnergy(renewableKwh, unit)} unit={unit} sublabel="Certified + Direct / Private Wire" missing={!hasData}
              currentNumeric={toDisplayUnit(renewableKwh, unit)} priorNumeric={toDisplayUnit(priorRenewableKwh, unit)} priorDigits={unit === "MWh" ? 2 : 0} lowerIsBetter={false} />
            <MetricCard accent="#0E7C66" label="Renewable %" value={fmtPct(renewablePct)} sublabel="Of total electricity" missing={!hasData}
              currentNumeric={renewablePct} priorNumeric={priorRenewablePct} lowerIsBetter={false} />
          </SimpleGrid>
        </Flex>
      </Grid>
    </Box>
  );
};

// Cycled through for the gas ring chart / table — number of fuels in the
// breakdown is data-driven, so we just pick a stable colour per index.
const GAS_PALETTE_FALLBACK = [
  "#0E7C66", "#34D399", "#60A5FA", "#0EA5E9", "#A78BFA",
  "#F59E0B", "#FB7185", "#94A3B8", "#475569", "#22D3EE",
];

// Shared shell for a "ring chart + fuel list + 2 metric cards" fuel-breakdown
// panel — Gas / Liquid fuels / Other are each a fixed, always-full bucket
// list for their carrier (Empty States), so they share this layout via the
// copy/accent props below rather than duplicating ~80 lines per carrier.
const FuelBreakdownRingSection = ({
  rows, totalKwh, priorRows, priorTotalKwh,
  title, info, emptyNote, listEmptyNote, listTitle, totalLabel, totalSublabel, accent, footerNote,
  topFuelSublabel, noDataSublabel, unit, isLoading,
}) => {
  if (isLoading) {
    return (
      <Box>
        <SectionHeading title={title} dataState="loading" />
        <SectionSkeleton variant="ring" />
      </Box>
    );
  }
  const hasData = rows.length > 0 && totalKwh > 0;
  const colored = rows.map((r, i) => ({
    ...r,
    color: GAS_PALETTE_FALLBACK[i % GAS_PALETTE_FALLBACK.length],
  }));
  const colorById = Object.fromEntries(colored.map((r) => [r.id, r.color]));
  const topFuel = colored[0];
  const topFuelPct = topFuel && totalKwh > 0 ? (topFuel.value / totalKwh) * 100 : 0;
  const priorTopFuelKwh = topFuel ? ((priorRows || []).find((r) => r.id === topFuel.id)?.value || 0) : 0;
  const priorTopFuelPct = (priorTotalKwh || 0) > 0 ? (priorTopFuelKwh / priorTotalKwh) * 100 : null;
  return (
    <Box>
      <SectionHeading
        title={title}
        info={info}
        dataState={hasData ? "real" : "missing"}
      />
      {!hasData && (
        <NotAvailableNote>
          {emptyNote}
        </NotAvailableNote>
      )}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
        <Box h="380px">
          <ConcentricRingChart
            rows={colored}
            height={320}
            formatValue={(v) => `${fmtEnergy(v, unit)} ${unit}`}
            stretch
          />
        </Box>
        <Flex direction="column" gap={3} h="380px" minW={0}>
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} flex="1" overflow="auto">
            <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>{listTitle}</Text>
            {colored.length === 0 ? (
              <Text fontSize="12px" color="gray.500">{listEmptyNote}</Text>
            ) : (
              <Flex direction="column" gap={2}>
                {colored.map((r) => {
                  const pct = totalKwh > 0 ? (r.value / totalKwh) * 100 : 0;
                  const priorVal = (priorRows || []).find((p) => p.id === r.id)?.value;
                  return (
                    <Flex key={r.id} align="center" gap={2} minW={0}>
                      <Box w="10px" h="10px" borderRadius="sm" bg={colorById[r.id]} flexShrink={0} />
                      <Text flex="1" fontSize="13px" color="gray.700" minW={0} noOfLines={1}>{r.id}</Text>
                      <Text fontSize="13px" color="gray.700" fontWeight="600" whiteSpace="nowrap" flexShrink={0}>{fmtEnergy(r.value, unit)} {unit}</Text>
                      <Text fontSize="12px" color="gray.500" minW="44px" textAlign="right" flexShrink={0}>{hasData ? `${pct.toFixed(1)}%` : "—"}</Text>
                      <Box minW="56px" display="flex" justifyContent="flex-end" flexShrink={0}>
                        <YoyPill
                          currentNumeric={toDisplayUnit(r.value, unit)}
                          priorNumeric={toDisplayUnit(priorVal, unit)}
                          unit={unit}
                          digits={unit === "MWh" ? 2 : 0}
                        />
                      </Box>
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </Box>
          <SimpleGrid minChildWidth="150px" spacing={3}>
            <MetricCard accent={accent} label={totalLabel} value={fmtEnergy(totalKwh, unit)} unit={unit} sublabel={totalSublabel} missing={!hasData}
              currentNumeric={toDisplayUnit(totalKwh, unit)} priorNumeric={toDisplayUnit(priorTotalKwh, unit)} priorDigits={unit === "MWh" ? 2 : 0} />
            <MetricCard
              accent="#0E7C66"
              label={topFuel ? `${topFuel.id} %` : "Top fuel %"}
              value={fmtPct(topFuelPct)}
              sublabel={topFuel ? topFuelSublabel : noDataSublabel}
              missing={!hasData}
              currentNumeric={topFuelPct}
              priorNumeric={priorTopFuelPct}
            />
          </SimpleGrid>
        </Flex>
      </Grid>
      <Text fontSize="11px" color="gray.500" mt={2}>
        {footerNote}
      </Text>
    </Box>
  );
};

// Gas carrier — natural gas, LNG, CNG, LPG, regardless of whether the fuel
// was entered under Stationary or Mobile (see FUEL_CARRIER_MAP). CNG always
// reads 0 today — no backend consumption_type for it yet (data gap, not a
// bug); still shown per Empty States.
const GasBreakdownSection = ({ rows, totalKwh, priorRows, priorTotalKwh, unit, isLoading }) => (
  <FuelBreakdownRingSection
    rows={rows}
    totalKwh={totalKwh}
    priorRows={priorRows}
    priorTotalKwh={priorTotalKwh}
    unit={unit}
    isLoading={isLoading}
    title="Gas breakdown"
    info="Gas carrier — natural gas, LNG, CNG, LPG. Volumetric / mass fuels converted to kWh via DESNZ-style calorific factors; rows already in kWh are used as-is. CNG has no backend fuel code yet, so it always reads 0."
    emptyNote="No gas has been recorded for the selected period."
    listTitle="Gas types"
    listEmptyNote="No gas recorded for the period."
    totalLabel="Total gas"
    totalSublabel="Sum of all gas types"
    accent={SECR_PALETTE.gas}
    footerNote="One row per named gas category (Natural gas / LNG / CNG / LPG), summed across Stationary and Mobile entries. Volumetric / mass fuels converted via DESNZ calorific factors; kWh-native rows used as-is."
    topFuelSublabel="Of total gas"
    noDataSublabel="No gas recorded"
  />
);

// Liquid fuels carrier — petrol, diesel, HVO, gas oil, heating oil/kerosene,
// aviation, marine. Marine has no backend consumption_type yet (data gap).
const LiquidFuelsBreakdownSection = ({ rows, totalKwh, priorRows, priorTotalKwh, unit, isLoading }) => (
  <FuelBreakdownRingSection
    rows={rows}
    totalKwh={totalKwh}
    priorRows={priorRows}
    priorTotalKwh={priorTotalKwh}
    unit={unit}
    isLoading={isLoading}
    title="Liquid fuels breakdown"
    info="Liquid fuels carrier — petrol, diesel, HVO, gas oil, heating oil/kerosene, aviation, marine. Volumetric fuels converted to kWh via DESNZ-style calorific factors. Marine has no backend fuel code yet, so it always reads 0."
    emptyNote="No liquid fuel has been recorded for the selected period."
    listTitle="Liquid fuel types"
    listEmptyNote="No liquid fuel recorded for the period."
    totalLabel="Total liquid fuels"
    totalSublabel="Sum of all liquid fuel types"
    accent={SECR_PALETTE.liquidFuels}
    footerNote="One row per named liquid-fuel category, summed across Stationary and Mobile entries — e.g. a generator's diesel and a van's diesel both count here. Converted via DESNZ calorific factors."
    topFuelSublabel="Of total liquid fuels"
    noDataSublabel="No liquid fuel recorded"
  />
);

// Other carrier — biomass, solid fuels, purchased heat, purchased steam,
// purchased cooling, plus Acetylene (a real fuel that doesn't fit any of the
// named buckets above). Solid fuels / purchased heat / steam / cooling have
// no backend consumption_type or entity anywhere yet (data gap).
const OtherBreakdownSection = ({ rows, totalKwh, priorRows, priorTotalKwh, unit, isLoading }) => (
  <FuelBreakdownRingSection
    rows={rows}
    totalKwh={totalKwh}
    priorRows={priorRows}
    priorTotalKwh={priorTotalKwh}
    unit={unit}
    isLoading={isLoading}
    title="Other breakdown"
    info="Other carrier — biomass, solid fuels, purchased heat, purchased steam, purchased cooling. Solid fuels / purchased heat / steam / cooling have no backend fuel code or data source yet, so they always read 0. Acetylene doesn't fit any named category (not biomass, not a liquid fuel) and gets its own line."
    emptyNote="No other-carrier fuel has been recorded for the selected period."
    listTitle="Other fuel types"
    listEmptyNote="No other-carrier fuel recorded for the period."
    totalLabel="Total other"
    totalSublabel="Sum of all other carrier types"
    accent={SECR_PALETTE.other}
    footerNote="One row per named category, summed across Stationary and Mobile entries. Biomass converted via DESNZ calorific factors."
    topFuelSublabel="Of total other"
    noDataSublabel="No fuel recorded"
  />
);

const TransportFuelSection = ({ rows, totalKwh, priorRows, priorTotalKwh, unit, isLoading }) => {
  if (isLoading) {
    return (
      <Box>
        <SectionHeading title="Transport fuel breakdown" dataState="loading" />
        <SectionSkeleton variant="ring" />
      </Box>
    );
  }
  const evKwh = rows.find((r) => r.id === "EV")?.value || 0;
  const evPct = totalKwh > 0 ? (evKwh / totalKwh) * 100 : 0;
  const priorEvKwh = (priorRows || []).find((r) => r.id === "EV")?.value || 0;
  const priorEvPct = (priorTotalKwh || 0) > 0 ? (priorEvKwh / priorTotalKwh) * 100 : null;
  const hasReal = rows.some((r) => r.value > 0);
  const hvoMissing = rows.find((r) => r.id === "HVO")?.value === 0;
  const greyMissing = rows.find((r) => r.id === "Grey fleet")?.value === 0;
  const partial = hvoMissing || greyMissing;
  // hvoMissing/greyMissing above are checked against the full fixed-bucket
  // `rows` (so the notes above can still say "no HVO recorded"); the chart
  // and list only render categories that actually have consumption.
  const visibleRows = rows.filter((r) => r.value > 0);
  const FUEL_PALETTE = {
    Diesel: "#475569", Petrol: "#94A3B8", HVO: "#0E7C66", "Other mobile fuel": "#B45309", EV: "#34D399", "Grey fleet": "#60A5FA",
  };
  // Decades rather than symlog's default linear ticks, which overlap at the top
  // of the axis. Passing the scale's constant also drops the decades below it,
  // which symlog squashes against the baseline. See decadeTickValues.
  const fuelTickValues = decadeTickValues(
    Math.max(0, ...visibleRows.map((r) => r.value)),
    SYMLOG_CONSTANT,
  );
  return (
    <Box>
      <SectionHeading
        title="Transport fuel breakdown"
        info="Mobile combustion fuel split, kWh-normalised. Diesel/Petrol/HVO converted from litres via DESNZ-style calorific factors. Other mobile fuel (e.g. LPG/autogas vehicles) covers every other mobile fuel type, converted via its own calorific factor. EV mileage comes from the ELECTRIC_CAR consumption rows. Grey fleet (business mileage in employee-owned vehicles) is sourced from Cat 6 Business Travel Cars and converted to kWh via the transport-average kg-CO₂e/kWh factor."
        dataState={hasReal ? (partial ? "partial" : "real") : "missing"}
      />
      {!hasReal && (
        <NotAvailableNote>
          No transport fuel rows recorded for the period.
        </NotAvailableNote>
      )}
      {hasReal && hvoMissing && (
        <NotAvailableNote>
          No HVO recorded — rows without an explicit HVO consumption-type code will fall under Diesel/Petrol.
        </NotAvailableNote>
      )}
      {hasReal && greyMissing && (
        <NotAvailableNote>
          No Cat 6 Business Travel Cars rows for the period — grey-fleet mileage may not have been captured.
        </NotAvailableNote>
      )}
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4}>
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} minW={0} h="380px">
          <ResponsiveBar
            data={visibleRows.map((r) => ({ fuel: r.id, kWh: r.value, debug: r.debug }))}
            keys={["kWh"]}
            indexBy="fuel"
            margin={{ top: 16, right: 24, bottom: 36, left: 80 }}
            padding={0.4}
            colors={({ data: d }) => FUEL_PALETTE[d.fuel] || "#94A3B8"}
            // symlog handles zeros AND keeps massive values (Diesel = 212M)
            // visible alongside small ones (Grey fleet = 413k) without
            // collapsing the small bars to invisible slivers. Constant
            // tunes the linear-vs-log transition; 100k matches the rough
            // size of our smallest expected non-zero bar.
            valueScale={{ type: "symlog", constant: SYMLOG_CONSTANT }}
            axisBottom={{ tickSize: 0, tickPadding: 6 }}
            gridYValues={fuelTickValues}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              tickValues: fuelTickValues,
              legend: `${unit} (log)`,
              legendOffset: -64,
              legendPosition: "middle",
              // Underlying bar data stays raw kWh (so the symlog tuning above
              // keeps working unchanged) — only the tick LABEL converts.
              format: (v) => {
                const dv = toDisplayUnit(v, unit);
                return dv >= 1_000_000 ? `${(dv / 1_000_000).toFixed(0)}M` : dv >= 1000 ? `${(dv / 1000).toFixed(0)}k` : fmtNum(dv, unit === "MWh" ? 1 : 0);
              },
            }}
            enableLabel={false}
            theme={chartTheme}
            tooltip={({ data: d }) => {
              const dbg = d.debug || { rows: 0, raw: 0, rawUnit: "" };
              const rawDigits = dbg.rawUnit === "tCO₂e" ? 2 : 0;
              const byFuelEntries = dbg.byFuel
                ? Object.entries(dbg.byFuel).sort((a, b) => b[1] - a[1])
                : [];
              return (
                <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px" maxW="240px">
                  <Text fontWeight="700" color="gray.800">{d.fuel}</Text>
                  <Text color="gray.600">{fmtEnergy(d.kWh, unit)} {unit}</Text>
                  <Text color="gray.500" fontSize="11px" mt={1}>
                    Source: {dbg.rows} row{dbg.rows === 1 ? "" : "s"}
                    {dbg.rows > 0 && dbg.rawUnit && (
                      <> · {fmtNum(dbg.raw, rawDigits)} {dbg.rawUnit}</>
                    )}
                  </Text>
                  {byFuelEntries.length > 0 && (
                    <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.100">
                      {byFuelEntries.map(([name, kwh]) => (
                        <Flex key={name} justify="space-between" gap={2}>
                          <Text color="gray.500" fontSize="11px">{name}</Text>
                          <Text color="gray.700" fontSize="11px" fontWeight="600" whiteSpace="nowrap">{fmtEnergy(kwh, unit)} {unit}</Text>
                        </Flex>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            }}
          />
        </Box>
        <Flex direction="column" gap={3} h="380px" minW={0}>
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} flex="1" overflow="auto">
            <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>Fuel breakdown</Text>
            <Flex direction="column" gap={2}>
              {visibleRows.map((r) => {
                const pct = totalKwh > 0 ? (r.value / totalKwh) * 100 : 0;
                const priorVal = (priorRows || []).find((p) => p.id === r.id)?.value;
                return (
                  <Flex key={r.id} align="center" gap={2} minW={0}>
                    <Box w="10px" h="10px" borderRadius="sm" bg={FUEL_PALETTE[r.id] || "#94A3B8"} flexShrink={0} />
                    <Text flex="1" fontSize="13px" color="gray.700" minW={0} noOfLines={1}>{r.id}</Text>
                    <Text fontSize="13px" color="gray.700" fontWeight="600" whiteSpace="nowrap" flexShrink={0}>{fmtEnergy(r.value, unit)} {unit}</Text>
                    <Text fontSize="12px" color="gray.500" minW="44px" textAlign="right" flexShrink={0}>{hasReal ? `${pct.toFixed(1)}%` : "—"}</Text>
                    <Box minW="56px" display="flex" justifyContent="flex-end" flexShrink={0}>
                      <YoyPill
                        currentNumeric={toDisplayUnit(r.value, unit)}
                        priorNumeric={toDisplayUnit(priorVal, unit)}
                        unit={unit}
                        digits={unit === "MWh" ? 2 : 0}
                        lowerIsBetter={r.id !== "EV"}
                      />
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          </Box>
          <SimpleGrid minChildWidth="150px" spacing={3}>
            <MetricCard accent={SECR_PALETTE.transport} label="Total transport" value={fmtEnergy(totalKwh, unit)} unit={unit} sublabel="Sum of all fuel types" missing={!hasReal}
              currentNumeric={toDisplayUnit(totalKwh, unit)} priorNumeric={toDisplayUnit(priorTotalKwh, unit)} priorDigits={unit === "MWh" ? 2 : 0} />
            <MetricCard accent="#34D399" label="EV %" value={fmtPct(evPct)} sublabel="Of total transport energy" missing={!hasReal}
              currentNumeric={evPct} priorNumeric={priorEvPct} lowerIsBetter={false} />
          </SimpleGrid>
        </Flex>
      </Grid>
      <Text fontSize="11px" color="gray.500" mt={2}>
        Volumetric fuels converted via standard DESNZ calorific factors (Diesel 10.96 kWh/L, Petrol 9.42 kWh/L, HVO 9.65 kWh/L). EV rows arrive in kWh.
      </Text>
    </Box>
  );
};

// Usage view (Section 3) — energy consumed in fixed equipment: boilers,
// generators, process plant, buildings. One row per fuel actually recorded
// under Stationary, across every carrier (dynamic discovery, like Transport's
// own bounded fuel set, rather than Section 2's fixed carrier buckets — an
// exhaustive list spanning all ~27 possible codes in one section would be
// unwieldy).
const StationaryFuelSection = ({ rows, totalKwh, priorRows, priorTotalKwh, unit, isLoading }) => (
  <FuelBreakdownRingSection
    rows={rows}
    totalKwh={totalKwh}
    priorRows={priorRows}
    priorTotalKwh={priorTotalKwh}
    unit={unit}
    isLoading={isLoading}
    title="Stationary fuel breakdown"
    info="Energy consumed in fixed equipment — boilers, generators, process plant, buildings. One row per fuel actually recorded, across all carriers."
    emptyNote="No stationary fuel has been recorded for the selected period."
    listTitle="Fuel types"
    listEmptyNote="No stationary fuel recorded for the period."
    totalLabel="Total stationary"
    totalSublabel="Sum of all stationary fuel types"
    accent={SECR_PALETTE.stationary}
    footerNote="One row per backend consumption type actually recorded under Stationary, across every carrier. Volumetric / mass fuels converted via DESNZ calorific factors; kWh-native rows used as-is."
    topFuelSublabel="Of total stationary"
    noDataSublabel="No fuel recorded"
  />
);

// Quarterly water chart — bullet-style nested vertical bars. Outer wide
// bar = supply (water consumed). Inner narrower bar (centred, half width)
// = discharge that went back down the drain. One cluster per quarter so
// users can read both the absolute volume and the discharge ratio at a
// glance. Pure SVG/Chakra so we can render the nested geometry — Nivo
// has no native "thick + thin overlapping" mode without custom layers.
const QuarterlyWaterChart = ({ supplyByQuarter, dischargeByQuarter, priorSupplyByQuarter, priorDischargeByQuarter, priorFy }) => {
  const quarters = ["Q1", "Q2", "Q3", "Q4"].map((q) => ({
    quarter: q,
    supply: Number(supplyByQuarter?.[q]) || 0,
    discharge: Number(dischargeByQuarter?.[q]) || 0,
    priorSupply: Number(priorSupplyByQuarter?.[q]) || 0,
    priorDischarge: Number(priorDischargeByQuarter?.[q]) || 0,
  }));
  const hasPrior = quarters.some((q) => q.priorSupply > 0 || q.priorDischarge > 0);
  const max = quarters.reduce(
    (acc, q) => Math.max(acc, q.supply, q.discharge, q.priorSupply, q.priorDischarge),
    0,
  ) || 1;
  // Round the y-axis up to a clean 4-step domain so the labels read as
  // round numbers rather than the raw max.
  const niceMax = (() => {
    const mag = Math.pow(10, Math.floor(Math.log10(max)));
    const norm = max / mag;
    const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
    return step * mag;
  })();
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * niceMax);
  const fmtAxis = (v) =>
    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v;

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="320px">
      <Flex h="100%" align="stretch" gap={6} pl={12} pr={2} pt={2} pb={6} position="relative">
        {/* Y-axis ticks + horizontal gridlines along the chart's full width */}
        <Box position="absolute" top="8px" bottom="40px" left={0} right="8px" pointerEvents="none">
          {ticks.slice().reverse().map((t, i) => {
            const pct = (i / (ticks.length - 1)) * 100;
            return (
              <Box key={t} position="absolute" left={0} right={0} top={`${pct}%`} transform="translateY(-50%)">
                <Text position="absolute" left={0} fontSize="11px" color="gray.500" transform="translateY(-50%)">
                  {fmtAxis(t)}
                </Text>
                <Box position="absolute" left="40px" right={0} top="50%" h="1px" bg="gray.100" />
              </Box>
            );
          })}
        </Box>

        {/* Bars */}
        {quarters.map(({ quarter, supply, discharge, priorSupply, priorDischarge }) => {
          const supplyPct = niceMax > 0 ? (supply / niceMax) * 100 : 0;
          const dischargePct = niceMax > 0 ? (discharge / niceMax) * 100 : 0;
          const priorSupplyPct = niceMax > 0 ? (priorSupply / niceMax) * 100 : 0;
          const priorDischargePct = niceMax > 0 ? (priorDischarge / niceMax) * 100 : 0;
          return (
            <Flex
              key={quarter}
              direction="column"
              align="center"
              flex="1"
              minW={0}
              ml={12}
              position="relative"
            >
              <Box flex="1" w="100%" position="relative">
                {/* Supply (outer wide bar — light sky so the darker
                    inner discharge bar stands out against it) */}
                <Tooltip label={`${quarter} · Supply: ${fmtNum(supply)} m³`} hasArrow placement="top">
                  <Box
                    position="absolute"
                    bottom={0}
                    left="50%"
                    transform="translateX(-50%)"
                    w="60%"
                    h={`${supplyPct}%`}
                    bg="#BAE6FD"
                    borderRadius="2px 2px 0 0"
                    cursor="default"
                  />
                </Tooltip>
                {/* Discharge (inner narrower bar, deeper blue) */}
                <Tooltip
                  label={`${quarter} · Discharge: ${fmtNum(discharge)} m³ (${supply > 0 ? ((discharge / supply) * 100).toFixed(1) : "—"}% of supply)`}
                  hasArrow
                  placement="top"
                >
                  <Box
                    position="absolute"
                    bottom={0}
                    left="50%"
                    transform="translateX(-50%)"
                    w="28%"
                    h={`${dischargePct}%`}
                    bg="#0369A1"
                    borderRadius="2px 2px 0 0"
                    cursor="default"
                  />
                </Tooltip>
                {/* Prior-year supply marker — dashed line spanning the
                    width of the supply bar at last year's height. */}
                {hasPrior && priorSupply > 0 && (
                  <Tooltip label={`${priorFy || "Prior"} ${quarter} · Supply: ${fmtNum(priorSupply)} m³`} hasArrow placement="top">
                    <Box
                      position="absolute"
                      bottom={`calc(${priorSupplyPct}% - 1px)`}
                      left="50%"
                      transform="translateX(-50%)"
                      w="64%"
                      h="2px"
                      borderTop="2px dashed"
                      borderColor="#0F172A"
                      cursor="default"
                    />
                  </Tooltip>
                )}
                {/* Prior-year discharge marker — solid amber line
                    spanning the width of the discharge bar. */}
                {hasPrior && priorDischarge > 0 && (
                  <Tooltip label={`${priorFy || "Prior"} ${quarter} · Discharge: ${fmtNum(priorDischarge)} m³`} hasArrow placement="top">
                    <Box
                      position="absolute"
                      bottom={`calc(${priorDischargePct}% - 1px)`}
                      left="50%"
                      transform="translateX(-50%)"
                      w="32%"
                      h="2px"
                      bg="#F59E0B"
                      cursor="default"
                    />
                  </Tooltip>
                )}
              </Box>
              <Text fontSize="12px" color="gray.700" fontWeight="600" mt={2}>{quarter}</Text>
            </Flex>
          );
        })}
      </Flex>
      {/* Legend */}
      <Flex justify="center" gap={4} mt={-3} flexWrap="wrap">
        <HStack spacing={1.5}>
          <Box w="12px" h="10px" bg="#BAE6FD" borderRadius="sm" />
          <Text fontSize="11px" color="gray.600">Supply (m³)</Text>
        </HStack>
        <HStack spacing={1.5}>
          <Box w="12px" h="10px" bg="#0369A1" borderRadius="sm" />
          <Text fontSize="11px" color="gray.600">Discharged (m³)</Text>
        </HStack>
        {hasPrior && (
          <>
            <HStack spacing={1.5}>
              <Box w="14px" h="0" borderTop="2px dashed" borderColor="#0F172A" />
              <Text fontSize="11px" color="gray.600">{priorFy || "Prior"} supply</Text>
            </HStack>
            <HStack spacing={1.5}>
              <Box w="14px" h="2px" bg="#F59E0B" />
              <Text fontSize="11px" color="gray.600">{priorFy || "Prior"} discharged</Text>
            </HStack>
          </>
        )}
      </Flex>
    </Box>
  );
};

const WaterSection = ({ supplyM3, dischargeM3, priorSupplyM3, priorDischargeM3, supplyByQuarter, dischargeByQuarter, priorSupplyByQuarter, priorDischargeByQuarter, priorFy, isLoading }) => {
  if (isLoading) {
    return (
      <Box>
        <SectionHeading title="Water" dataState="loading" />
        <SectionSkeleton variant="bars" />
        <Skeleton height="320px" borderRadius="md" />
      </Box>
    );
  }
  const hasSupply = supplyM3 > 0;
  const hasDischarge = dischargeM3 > 0;
  const hasAny = hasSupply || hasDischarge;
  const state = hasSupply && hasDischarge ? "real" : hasAny ? "partial" : "missing";
  return (
    <Box>
      <SectionHeading
        title="Water"
        info="Water supply (Cat 1 purchased water) + wastewater discharge (Cat 5). Best-practice extension to SECR."
        dataState={state}
      />
      {!hasAny && (
        <NotAvailableNote>
          Neither water supply (Scope 3 Cat 1) nor wastewater (Scope 3 Cat 5) has been recorded for the period.
        </NotAvailableNote>
      )}
      {hasAny && !hasDischarge && (
        <NotAvailableNote>
          No wastewater volume recorded — only supply is shown.
        </NotAvailableNote>
      )}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
        <MetricCard accent={SECR_PALETTE.water} label="Water consumed" value={fmtNum(supplyM3)} unit="m³" missing={!hasSupply}
          currentNumeric={supplyM3} priorNumeric={priorSupplyM3} />
        <MetricCard
          accent="#0EA5E9"
          label="Discharged to drain"
          value={fmtNum(dischargeM3)}
          unit="m³"
          sublabel={hasSupply ? `${((dischargeM3 / supplyM3) * 100).toFixed(1)}% of water consumed` : undefined}
          missing={!hasDischarge}
          currentNumeric={hasSupply ? (dischargeM3 / supplyM3) * 100 : dischargeM3}
          priorNumeric={priorSupplyM3 > 0 ? (priorDischargeM3 / priorSupplyM3) * 100 : priorDischargeM3}
          priorDigits={hasSupply ? 1 : 0}
          priorLabel="Prior year discharge ratio"
        />
        <MetricCard accent="#7DD3FC" label="Intensity" value="n/a" unit="m³ / FTE" sublabel="FTE not tracked" missing />
      </SimpleGrid>
      {hasAny && supplyByQuarter && (
        <QuarterlyWaterChart
          supplyByQuarter={supplyByQuarter}
          dischargeByQuarter={dischargeByQuarter}
          priorSupplyByQuarter={priorSupplyByQuarter}
          priorDischargeByQuarter={priorDischargeByQuarter}
          priorFy={priorFy}
        />
      )}
      <Text fontSize="11px" color="gray.500" mt={2}>
        Quarterly supply / discharge bars show the real per-quarter totals and appear only on the whole-year view; pick a
        specific quarter to see just its figure. Water supply emissions reported in Scope 3 Cat 1; wastewater treatment
        emissions reported in Scope 3 Cat 5.
      </Text>
    </Box>
  );
};

// Format an enum-style code (e.g. "AVERAGE_PLASTIC_FILM") for display
// in the sankey labels.
const prettyWasteLabel = (code) => {
  const s = String(code || "Unspecified");
  return s
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// 3-tier waste sankey on consumption tonnes (not emissions): Activity →
// Type → Disposal route. Wastewater is intentionally excluded — it has
// its own dedicated section and would otherwise dwarf the solid-waste
// flows visually.
const WasteFlowSankey = ({ items }) => {
  const { nodes, links, nodeLabel, grandTotal } = useMemo(() => {
    const built = { nodes: [], links: [] };
    const seen = new Set();
    const addNode = (id, color) => {
      if (seen.has(id)) return;
      seen.add(id);
      built.nodes.push({ id, nodeColor: color });
    };
    const labelMap = new Map();

    const cells = new Map();
    (items || []).forEach((it) => {
      const tonnes = Number(it.weight) || 0;
      if (tonnes <= 0) return;
      const activity = prettyWasteLabel(it.wasteActivity || "Unspecified");
      const wasteType = prettyWasteLabel(it.wasteType || "Unspecified");
      const disposal = prettyWasteLabel(it.disposalMethod || "Unspecified");
      const key = `${activity}||${wasteType}||${disposal}`;
      cells.set(key, (cells.get(key) || 0) + tonnes);
    });

    cells.forEach((value, key) => {
      const [activity, wasteType, disposal] = key.split("||");
      const activityId = `A:${activity}`;
      const typeId = `T:${activity}::${wasteType}`;
      const disposalId = `D:${disposal}`;
      addNode(activityId, "#8B5CF6");
      addNode(typeId, "#3B82F6");
      addNode(disposalId, "#14B8A6");
      labelMap.set(activityId, activity);
      labelMap.set(typeId, wasteType);
      labelMap.set(disposalId, disposal);
      built.links.push({ source: activityId, target: typeId, value });
      built.links.push({ source: typeId, target: disposalId, value });
    });

    const total = built.links
      .filter((l) => String(l.source).startsWith("A:"))
      .reduce((acc, l) => acc + (Number(l.value) || 0), 0);

    const safe = toSafariSafeSankey(built.nodes, built.links, (id) => labelMap.get(id) || id);
    return { nodes: safe.nodes, links: safe.links, nodeLabel: safe.labels, grandTotal: total };
  }, [items]);

  if (!nodes || nodes.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          No waste data has been entered for this period.
        </Text>
      </Box>
    );
  }

  const leftNodeCount = nodes.filter((n) => String(n.origId).startsWith("A:")).length;
  const heightPx = Math.max(360, Math.min(620, leftNodeCount * 60 + 120));
  const formatPct = (n, d) => (!d || d <= 0 ? null : `${((n / d) * 100).toFixed(1)}%`);
  const fmtTonnes = (v) => `${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t`;

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={`${heightPx}px`}>
      <ResponsiveSankey
        data={{ nodes: nodes.map(({ id, nodeColor }) => ({ id, nodeColor })), links }}
        margin={{ top: 12, right: 200, bottom: 12, left: 160 }}
        align="justify"
        sort="input"
        colors={(node) => node.nodeColor}
        nodeOpacity={1}
        nodeHoverOthersOpacity={0.35}
        nodeThickness={14}
        nodeSpacing={14}
        nodeBorderWidth={0}
        nodeBorderRadius={2}
        linkOpacity={0.45}
        linkHoverOthersOpacity={0.1}
        linkContract={2}
        enableLinkGradient
        labelPosition="outside"
        labelOrientation="horizontal"
        labelPadding={8}
        labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
        label={(node) => nodeLabel.get(node.id) || node.id}
        theme={chartTheme}
        nodeTooltip={({ node }) => (
          <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
            <Text fontWeight="700" color="gray.800">{nodeLabel.get(node.id) || node.id}</Text>
            <Text color="gray.600">
              {fmtTonnes(node.value)}
              {formatPct(node.value, grandTotal) && (
                <Text as="span" color="gray.500" ml={2}>· {formatPct(node.value, grandTotal)}</Text>
              )}
            </Text>
          </Box>
        )}
        linkTooltip={({ link }) => (
          <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
            <Text fontWeight="700" color="gray.800">
              {(nodeLabel.get(link.source.id) || link.source.id)} → {(nodeLabel.get(link.target.id) || link.target.id)}
            </Text>
            <Text color="gray.600">{fmtTonnes(link.value)}</Text>
          </Box>
        )}
      />
    </Box>
  );
};

const WasteViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Summary", value: "summary", icon: <ListBullets size={14} /> },
    { label: "Flow", value: "flow", icon: <FlowArrow size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

const WasteSection = ({ rows, totalTonnes, priorRows, priorTotalTonnes, items, isLoading }) => {
  const [view, setView] = useState("summary");
  // Derive waste-by-category rows for the second ring chart from the raw
  // items, bucketed by wasteActivity (with a friendly label fallback).
  const categoryRows = useMemo(() => buildWasteCategories(items), [items]);
  if (isLoading) {
    return (
      <Box>
        <SectionHeading title="Waste" dataState="loading" />
        <SectionSkeleton variant="ring" />
      </Box>
    );
  }
  const hasData = totalTonnes > 0;
  const priorByRoute = new Map((priorRows || []).map((r) => [r.id, r.value]));
  // Recycled / Other recovery — higher is better; landfill / incineration —
  // lower is better. Total waste — lower is better. Per-route routing flips
  // the lowerIsBetter flag accordingly.
  const lowerIsBetterByRoute = {
    Recycled: false,
    "Other recovery": false,
    Incineration: true,
    Landfill: true,
  };
  const ROUTE_PALETTE = {
    Recycled: "#34D399",
    "Other recovery": "#60A5FA",
    Incineration: "#94A3B8",
    Landfill: "#475569",
  };
  const CATEGORY_PALETTE_FALLBACK = [
    "#0E7C66", "#34D399", "#60A5FA", "#0EA5E9", "#A78BFA",
    "#F59E0B", "#FB7185", "#94A3B8", "#475569", "#22D3EE",
  ];
  const categoryRowsForChart = categoryRows
    .filter((r) => r.value > 0)
    .map((r, i) => ({ ...r, color: CATEGORY_PALETTE_FALLBACK[i % CATEGORY_PALETTE_FALLBACK.length] }));

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
        <SectionHeading
          title="Waste"
          info="Waste generated and routed by disposal method, summed across all questionnaires for the period (Scope 3 Cat 5 items endpoint)."
          dataState={hasData ? "real" : "missing"}
        />
        <WasteViewToggle view={view} setView={setView} />
      </Flex>
      {!hasData && (
        <NotAvailableNote>
          No waste-generated-in-operations items recorded for the period.
        </NotAvailableNote>
      )}
      {view === "summary" ? (
        <Flex direction="column" gap={4}>
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
            <Flex direction="column" gap={3} minW={0}>
              <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                <Text fontSize="13px" fontWeight="600" color="gray.700" mb={2}>Disposal routes</Text>
                <ConcentricRingChart
                  rows={rows.filter((r) => r.value > 0).map((r) => ({ ...r, color: ROUTE_PALETTE[r.id] || "#94A3B8" }))}
                  height={320}
                  formatValue={(v) => `${fmtNum(v, 2)} t`}
                  bare
                />
              </Box>
              <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>Disposal routes</Text>
                <Flex direction="column" gap={2}>
                  {rows.map((r) => {
                    const pct = totalTonnes > 0 ? (r.value / totalTonnes) * 100 : 0;
                    const priorVal = priorByRoute.get(r.id);
                    return (
                      <Flex key={r.id} align="center" gap={3}>
                        <Box w="10px" h="10px" borderRadius="sm" bg={ROUTE_PALETTE[r.id] || "#94A3B8"} flexShrink={0} />
                        <Text flex="1" fontSize="13px" color="gray.700">{r.id}</Text>
                        <Text fontSize="13px" color="gray.700" fontWeight="600">{fmtNum(r.value, 2)} t</Text>
                        <Text fontSize="12px" color="gray.500" minW="48px" textAlign="right">{hasData ? `${pct.toFixed(1)}%` : "—"}</Text>
                        <Box minW="64px" display="flex" justifyContent="flex-end">
                          <YoyPill
                            currentNumeric={r.value}
                            priorNumeric={priorVal}
                            unit="t"
                            digits={2}
                            lowerIsBetter={lowerIsBetterByRoute[r.id] ?? true}
                          />
                        </Box>
                      </Flex>
                    );
                  })}
                </Flex>
              </Box>
            </Flex>
            <Flex direction="column" gap={3} minW={0}>
              <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                <Text fontSize="13px" fontWeight="600" color="gray.700" mb={2}>Categories of waste</Text>
                <ConcentricRingChart
                  rows={categoryRowsForChart}
                  height={320}
                  formatValue={(v) => `${fmtNum(v, 2)} t`}
                  bare
                />
              </Box>
              <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>Categories of waste</Text>
                {categoryRowsForChart.length === 0 ? (
                  <Text fontSize="12px" color="gray.500">No waste recorded for the period.</Text>
                ) : (
                  <Flex direction="column" gap={2}>
                    {categoryRowsForChart.map((r) => {
                      const pct = totalTonnes > 0 ? (r.value / totalTonnes) * 100 : 0;
                      return (
                        <Flex key={r.id} align="center" gap={3}>
                          <Box w="10px" h="10px" borderRadius="sm" bg={r.color} flexShrink={0} />
                          <Text flex="1" fontSize="13px" color="gray.700">{r.id}</Text>
                          <Text fontSize="13px" color="gray.700" fontWeight="600">{fmtNum(r.value, 2)} t</Text>
                          <Text fontSize="12px" color="gray.500" minW="48px" textAlign="right">{hasData ? `${pct.toFixed(1)}%` : "—"}</Text>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
              </Box>
            </Flex>
          </Grid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <MetricCard accent={SECR_PALETTE.waste} label="Total waste" value={fmtNum(totalTonnes, 2)} unit="t" missing={!hasData}
              currentNumeric={totalTonnes} priorNumeric={priorTotalTonnes} priorDigits={2} />
            <MetricCard
              accent="#34D399"
              label="Recycled %"
              value={fmtPct(totalTonnes > 0 ? ((rows.find((r) => r.id === "Recycled")?.value || 0) / totalTonnes) * 100 : 0)}
              sublabel="Of total waste"
              missing={!hasData}
              currentNumeric={totalTonnes > 0 ? ((rows.find((r) => r.id === "Recycled")?.value || 0) / totalTonnes) * 100 : 0}
              priorNumeric={priorTotalTonnes > 0 ? ((priorByRoute.get("Recycled") || 0) / priorTotalTonnes) * 100 : null}
              lowerIsBetter={false}
            />
          </SimpleGrid>
        </Flex>
      ) : (
        <WasteFlowSankey items={items} />
      )}
      <Text fontSize="11px" color="gray.500" mt={2}>
        Flow widths are tonnes of waste consumed (not emissions). Wastewater is shown separately in the Water section. Waste emissions reported in Scope 3 Cat 5.
      </Text>
    </Box>
  );
};

const IntensitySection = ({ current, prior, currentFy, priorFy, totalKgCo2eLB, priorTotalKgCo2eLB, supplyM3, wasteTotalTonnes }) => {
  // We can compute energy + emissions intensity per any denominator we
  // have; the platform currently doesn't store FTE or revenue, so all
  // the per-employee / per-£m rows render as n/a until those land.
  const rows = [
    { label: "kWh per employee",            current: null, prior: null, lower: true, digits: 0 },
    { label: "kWh per £m revenue",          current: null, prior: null, lower: true, digits: 0 },
    { label: "kgCO₂e per employee (LB)",    current: null, prior: null, lower: true, digits: 0 },
    { label: "kgCO₂e per £m revenue (LB)",  current: null, prior: null, lower: true, digits: 0 },
    { label: "m³ water per employee",       current: null, prior: null, lower: true, digits: 1 },
    { label: "kg waste per employee",       current: null, prior: null, lower: true, digits: 1 },
  ];
  return (
    <Box>
      <SectionHeading
        title="Intensity metrics"
        info="SECR mandatory. Ratios normalise consumption and emissions by an organisation-relevant denominator. Requires FTE headcount and revenue figures, which the platform does not currently track."
        dataState="missing"
      />
      <NotAvailableNote>
        FTE and revenue denominators are not stored on the org / questionnaire models, so the SECR-mandatory intensity
        ratios can't be computed from existing data. Adding them needs a settings page (or per-year disclosure inputs)
        before this table can populate.
      </NotAvailableNote>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">Metric</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">{currentFy}</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">{priorFy}</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">YoY</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => (
              <Tr key={row.label} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                <Td py={3} px={4} fontSize="14px" color="gray.700" fontWeight="500">{row.label}</Td>
                <Td py={3} px={4} fontSize="14px" color="gray.500" textAlign="right">n/a</Td>
                <Td py={3} px={4} fontSize="13px" color="gray.500" textAlign="right">n/a</Td>
                <Td py={3} px={4} textAlign="left" minW="120px">
                  <Text fontSize="11px" color="gray.400">denominator missing</Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

// Summarise the real Scope 2 v2 on-site generation rows returned by
// /insights/consumption (one row per fuel/tech installation, with the real
// self-consumed / exported split and certificate figures). No apportionment —
// every column is a persisted value.
const summariseOnsite = (rows) => {
  if (!rows || rows.length === 0) return null;
  const sum = (k) => rows.reduce((acc, r) => acc + (Number(r[k]) || 0), 0);
  return {
    rows,
    totals: {
      totalGenerated: sum("totalGenerated"),
      selfConsumed: sum("selfConsumed"),
      exportedToGrid: sum("exportedToGrid"),
      certificatesRetained: sum("certificatesRetained"),
      certificatesTransferred: sum("certificatesTransferred"),
    },
  };
};

const ONSITE_TH_PROPS = {
  py: 3,
  px: 4,
  fontSize: "11px",
  color: "gray.500",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: "600",
};

const OnsiteGenerationSection = ({ rows, priorRows, unit, isLoading }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope2V2",
  });
  const detail = useMemo(() => summariseOnsite(rows), [rows]);
  const priorDetail = useMemo(() => summariseOnsite(priorRows), [priorRows]);
  const hasGeneration = !!detail;
  const tot = detail?.totals;
  const pTot = priorDetail?.totals;

  if (isLoading) {
    return (
      <Box>
        <SectionHeading title="Onsite generation" dataState="loading" />
        <SectionSkeleton variant="ring" />
      </Box>
    );
  }

  return (
    <Box>
      <SectionHeading
        title="Onsite generation"
        info="Self-generated renewable electricity and associated certificates, mirroring the Scope 2 on-site generation task. Disclosure only — does not form part of Scope 2 purchased electricity (GHG Protocol Scope 2 Guidance Chapter 5)."
        dataState={hasGeneration ? "real" : "missing"}
      />
      {!hasGeneration && (
        <NotAvailableNote>
          No on-site generation rows recorded for the period.
        </NotAvailableNote>
      )}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
        <MetricCard accent="#0E7C66" label={t("columns.totalGenerated").replace(" (kWh)", "")} value={fmtEnergy(tot?.totalGenerated, unit)} unit={unit} sublabel="From onsite installations" missing={!hasGeneration}
          currentNumeric={toDisplayUnit(tot?.totalGenerated, unit)} priorNumeric={toDisplayUnit(pTot?.totalGenerated, unit)} priorDigits={unit === "MWh" ? 2 : 0} lowerIsBetter={false} priorLabel={`FY prior`} />
        <MetricCard accent="#34D399" label={t("columns.selfConsumed").replace(" (kWh)", "")} value={fmtEnergy(tot?.selfConsumed, unit)} unit={unit} sublabel="Used on site" missing={!hasGeneration}
          currentNumeric={toDisplayUnit(tot?.selfConsumed, unit)} priorNumeric={toDisplayUnit(pTot?.selfConsumed, unit)} priorDigits={unit === "MWh" ? 2 : 0} lowerIsBetter={false} />
        <MetricCard accent="#60A5FA" label={t("columns.exportedToGrid").replace(" (kWh)", "")} value={fmtEnergy(tot?.exportedToGrid, unit)} unit={unit} sublabel="Sent to grid" missing={!hasGeneration}
          currentNumeric={toDisplayUnit(tot?.exportedToGrid, unit)} priorNumeric={toDisplayUnit(pTot?.exportedToGrid, unit)} priorDigits={unit === "MWh" ? 2 : 0} lowerIsBetter={false} />
        <MetricCard accent="#0EA5E9" label={t("columns.certificatesRetained").replace(" (kWh)", "")} value={fmtEnergy(tot?.certificatesRetained, unit)} unit={unit} sublabel="Kept for market-based" missing={!hasGeneration}
          currentNumeric={toDisplayUnit(tot?.certificatesRetained, unit)} priorNumeric={toDisplayUnit(pTot?.certificatesRetained, unit)} priorDigits={unit === "MWh" ? 2 : 0} lowerIsBetter={false} />
        <MetricCard accent="#A78BFA" label={t("columns.certificatesTransferred").replace(" (kWh)", "")} value={fmtEnergy(tot?.certificatesTransferred, unit)} unit={unit} sublabel="Sold / transferred" missing={!hasGeneration}
          currentNumeric={toDisplayUnit(tot?.certificatesTransferred, unit)} priorNumeric={toDisplayUnit(pTot?.certificatesTransferred, unit)} priorDigits={unit === "MWh" ? 2 : 0} />
      </SimpleGrid>
      {hasGeneration && detail && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" overflowX="auto">
          <Table size="sm" variant="unstyled" minW="1000px">
            <Thead>
              <Tr bg="gray.50">
                <Th {...ONSITE_TH_PROPS} textAlign="left">{t("columns.fuelType")}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="right">{t("columns.totalGenerated").replace("(kWh)", `(${unit})`)}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="right">{t("columns.selfConsumed").replace("(kWh)", `(${unit})`)}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="right">{t("columns.exportedToGrid").replace("(kWh)", `(${unit})`)}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="left">{t("columns.certificatesIssued")}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="left">{t("columns.certificateType")}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="right">{t("columns.certificatesRetained").replace("(kWh)", `(${unit})`)}</Th>
                <Th {...ONSITE_TH_PROPS} textAlign="right">{t("columns.certificatesTransferred").replace("(kWh)", `(${unit})`)}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {detail.rows.map((row, idx) => {
                const issued = row.certificatesIssued;
                const certLabel = issued
                  ? t(`certificateTypes.${row.certificateType}`, { defaultValue: prettyFuelLabel(row.certificateType) })
                  : "";
                return (
                  <Tr key={idx} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                    <Td py={3} px={4} fontSize="14px" color="gray.700" fontWeight="500">{t(`fuelTypes.${row.fuelType}`, { defaultValue: prettyFuelLabel(row.fuelType) })}</Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{fmtEnergy(row.totalGenerated, unit)}</Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700" textAlign="right">{fmtEnergy(row.selfConsumed, unit)}</Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700" textAlign="right">{fmtEnergy(row.exportedToGrid, unit)}</Td>
                    <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"}>{issued ? "Yes" : "No"}</Td>
                    <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"}>{issued ? certLabel : "—"}</Td>
                    <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"} fontWeight="600" textAlign="right">{issued ? fmtEnergy(row.certificatesRetained, unit) : "—"}</Td>
                    <Td py={3} px={4} fontSize="14px" color={issued ? "gray.700" : "gray.400"} fontWeight="600" textAlign="right">{issued ? fmtEnergy(row.certificatesTransferred, unit) : "—"}</Td>
                  </Tr>
                );
              })}
              <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                <Td py={3} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">Total</Td>
                <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtEnergy(tot?.totalGenerated, unit)}</Td>
                <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtEnergy(tot?.selfConsumed, unit)}</Td>
                <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtEnergy(tot?.exportedToGrid, unit)}</Td>
                <Td py={3} px={4} />
                <Td py={3} px={4} />
                <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtEnergy(tot?.certificatesRetained, unit)}</Td>
                <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtEnergy(tot?.certificatesTransferred, unit)}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      )}
      <Text fontSize="11px" color="gray.500" mt={2}>
        Generated · total renewable electricity produced on site. Self consumed · used on site (already in net
        consumption). Exported · sent to the grid. Certificates retained / transferred determine how the generation
        is treated in your market-based Scope 2 figure. Onsite generation is disclosed separately and does not form
        part of the Scope 2 purchased electricity calculation per GHG Protocol Scope 2 Guidance Chapter 5.
      </Text>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Top-level dashboard
// ---------------------------------------------------------------------------

const SecrConsumptionDashboard = ({
  year,
  setYear,
  dimension,
  setDimension,
  quarter,
  setQuarter,
}) => {
  const fyLabel = `FY${year}`;
  const priorFy = `FY${year - 1}`;
  // Intensity metrics are unfinished (FTE/revenue denominators aren't stored yet),
  // so the section is gated to ESG admins until the data-capture work lands (ESG-3943 follow-up).
  const { isESGAdmin } = useAirUserRoles();

  const dimensionParams = useMemo(() => {
    if (!dimension) return "";
    return Object.entries(dimension)
      .filter(([_, ids]) => Array.isArray(ids) && ids.length > 0)
      .map(([key, ids]) => `${key}=${ids.join(",")}`)
      .join("&");
  }, [dimension]);

  // Single source for the whole tab: /insights/consumption per year, honoring the
  // dimension filter AND the quarter. Index [0] = selected year, [1] = prior year
  // (YoY), [2..4] = the older years for the 5-year energy trend.
  const multi = useGetMultiYearConsumption(
    [year, year - 1, year - 2, year - 3, year - 4],
    dimensionParams,
    {
      enabled: !!year,
      cacheTime: 60_000,
    },
    quarter,
  );
  const current = multi[0]?.data;
  const prior = multi[1]?.data;
  const prior2 = multi[2]?.data;
  const prior3 = multi[3]?.data;
  const prior4 = multi[4]?.data;
  // True only while THIS (year, dimension, quarter) combination has never
  // resolved before — react-query's isLoading, not isFetching, so switching
  // back to an already-cached period shows that cached data immediately
  // rather than re-flashing a skeleton. Every section below shares this one
  // flag since they all come from the same /insights/consumption call.
  const isLoading = !!multi[0]?.isLoading;

  // The energy builders read `insights.scopeOneAndTwoDetail`; the consumption
  // payload carries the same shape under `.energy`, so wrap each year to reuse
  // the builders unchanged. We also carry the Scope 2 v2 per-category electricity
  // breakdown (`.electricityV2`) so the electricity builders can prefer it — a v2
  // org has NO rows in `energy.electricity` (that list is mapped only from the v1
  // ReportElectricityItem table), so without this the electricity card + breakdown
  // are blank even though the data is present (and shows on the Scope 2 drill-down).
  const wrapEnergy = (dto) =>
    dto?.energy ? { scopeOneAndTwoDetail: dto.energy, electricityV2: dto.electricityV2 || [] } : null;
  const currentInsights = useMemo(() => wrapEnergy(current), [current]);
  const priorInsights = useMemo(() => wrapEnergy(prior), [prior]);
  const prior2Insights = useMemo(() => wrapEnergy(prior2), [prior2]);
  const prior3Insights = useMemo(() => wrapEnergy(prior3), [prior3]);
  const prior4Insights = useMemo(() => wrapEnergy(prior4), [prior4]);

  // Grey-fleet kWh is derived server-side from Cat 6 car emissions (no stored
  // distance); the DTO carries the derived kWh plus its provenance.
  const greyFleetKwh = current?.greyFleet?.derivedKwh || 0;
  const priorGreyFleetKwh = prior?.greyFleet?.derivedKwh || 0;
  const greyFleetStats = useMemo(
    () => ({ rows: current?.greyFleet?.sourceRows || 0, tCO2e: current?.greyFleet?.tCO2e || 0 }),
    [current],
  );
  const priorGreyFleetStats = useMemo(
    () => ({ rows: prior?.greyFleet?.sourceRows || 0, tCO2e: prior?.greyFleet?.tCO2e || 0 }),
    [prior],
  );
  const energy = useMemo(() => buildEnergyTotals(currentInsights, greyFleetKwh), [currentInsights, greyFleetKwh]);
  const priorEnergy = useMemo(() => buildEnergyTotals(priorInsights, priorGreyFleetKwh), [priorInsights, priorGreyFleetKwh]);
  const elecRows = useMemo(() => buildElectricityBreakdown(currentInsights), [currentInsights]);
  const priorElecRows = useMemo(() => buildElectricityBreakdown(priorInsights), [priorInsights]);
  const gasRows = useMemo(() => buildGasBreakdown(currentInsights), [currentInsights]);
  const priorGasRows = useMemo(() => buildGasBreakdown(priorInsights), [priorInsights]);
  const liquidFuelsRows = useMemo(() => buildLiquidFuelsBreakdown(currentInsights), [currentInsights]);
  const priorLiquidFuelsRows = useMemo(() => buildLiquidFuelsBreakdown(priorInsights), [priorInsights]);
  const otherCarrierRows = useMemo(() => buildOtherCarrierBreakdown(currentInsights), [currentInsights]);
  const priorOtherCarrierRows = useMemo(() => buildOtherCarrierBreakdown(priorInsights), [priorInsights]);
  const fuelRows = useMemo(
    () => buildFuelBreakdown(currentInsights, greyFleetKwh, greyFleetStats),
    [currentInsights, greyFleetKwh, greyFleetStats],
  );
  const priorFuelRows = useMemo(
    () => buildFuelBreakdown(priorInsights, priorGreyFleetKwh, priorGreyFleetStats),
    [priorInsights, priorGreyFleetKwh, priorGreyFleetStats],
  );
  // Section 3 — Stationary usage breakdown (every carrier, Stationary group only).
  const stationaryRows = useMemo(() => buildStationaryBreakdown(currentInsights), [currentInsights]);
  const priorStationaryRows = useMemo(() => buildStationaryBreakdown(priorInsights), [priorInsights]);

  // Waste rows now come straight from the consumption payload (weight +
  // disposalMethod + wasteActivity + wasteType), already dimension/quarter filtered.
  const wasteItems = useMemo(() => current?.waste || [], [current]);
  const wasteRoutes = useMemo(() => buildWasteRoutes(wasteItems), [wasteItems]);
  const priorWasteRoutes = useMemo(() => buildWasteRoutes(prior?.waste || []), [prior]);
  const wasteTotalTonnes = useMemo(
    () => wasteRoutes.reduce((acc, r) => acc + r.value, 0),
    [wasteRoutes],
  );
  const priorWasteTotalTonnes = useMemo(
    () => priorWasteRoutes.reduce((acc, r) => acc + r.value, 0),
    [priorWasteRoutes],
  );

  // On-site generation rows — real per-fuel figures with the real self-consumed /
  // exported split and certificates.
  const onsiteRows = useMemo(() => current?.onsite || [], [current]);
  const priorOnsiteRows = useMemo(() => prior?.onsite || [], [prior]);

  // Water is m³-normalised server-side. supplyByQuarter/dischargeByQuarter are
  // present only for a whole-year request; for a single quarter they are null and
  // the quarterly chart is hidden (no fabricated even split).
  const supplyM3 = Number(current?.water?.supplyM3) || 0;
  const dischargeM3 = Number(current?.water?.dischargeM3) || 0;
  const priorSupplyM3 = Number(prior?.water?.supplyM3) || 0;
  const priorDischargeM3 = Number(prior?.water?.dischargeM3) || 0;
  const supplyByQuarter = current?.water?.supplyByQuarter || null;
  const dischargeByQuarter = current?.water?.dischargeByQuarter || null;
  const priorSupplyByQuarter = prior?.water?.supplyByQuarter || null;
  const priorDischargeByQuarter = prior?.water?.dischargeByQuarter || null;

  const elecTotalKwh = elecRows.reduce((acc, r) => acc + r.value, 0);
  const priorElecTotalKwh = priorElecRows.reduce((acc, r) => acc + r.value, 0);

  // Mixed v1/v2 electricity in the SELECTED (dimension + quarter filtered) period:
  // the electricity card/breakdown show the v2 data and hide v1 ("v2 wins"), so warn
  // and quantify the hidden v1 kWh. Reads the raw payload (not the bucketed rows).
  const mixedV1V2Electricity = useMemo(
    () => isMixedScopeTwoElectricity(current?.energy?.electricity, current?.electricityV2),
    [current],
  );
  const hiddenV1ElecKwh = useMemo(
    () => (mixedV1V2Electricity ? v1PurchasedElectricityKwh(current?.energy?.electricity) : 0),
    [mixedV1V2Electricity, current],
  );

  const gasTotalKwh = gasRows.reduce((acc, r) => acc + r.value, 0);
  const priorGasTotalKwh = priorGasRows.reduce((acc, r) => acc + r.value, 0);
  const liquidFuelsTotalKwh = liquidFuelsRows.reduce((acc, r) => acc + r.value, 0);
  const priorLiquidFuelsTotalKwh = priorLiquidFuelsRows.reduce((acc, r) => acc + r.value, 0);
  const otherCarrierTotalKwh = otherCarrierRows.reduce((acc, r) => acc + r.value, 0);
  const priorOtherCarrierTotalKwh = priorOtherCarrierRows.reduce((acc, r) => acc + r.value, 0);
  const transportTotalKwh = fuelRows.reduce((acc, r) => acc + r.value, 0);
  const priorTransportTotalKwh = priorFuelRows.reduce((acc, r) => acc + r.value, 0);
  const stationaryTotalKwh = stationaryRows.reduce((acc, r) => acc + r.value, 0);
  const priorStationaryTotalKwh = priorStationaryRows.reduce((acc, r) => acc + r.value, 0);
  const hasEnergyData = !!energy && energy.totalKwh > 0;
  // Section 1 chart/table switch — table view lazily mounts CarrierQuarterlyTable
  // (its own 4-5 API calls fire only once selected, matching ScopeTwoVisualisation).
  const [energyView, setEnergyView] = useState("chart");
  // Page-wide kWh/MWh master switch (UnitToggle) — passed down to every
  // section below that displays an energy figure.
  const [unit, setUnit] = useState("kWh");

  // 5-year line chart series. Uses the same energy total derivation for
  // each year so the values reconcile with the headline tile. Years with
  // no recorded data are dropped from the series rather than rendered as
  // zero — a missing point is more honest than a flat-line dip.
  const prior2Energy = useMemo(() => buildEnergyTotals(prior2Insights, 0), [prior2Insights]);
  const prior3Energy = useMemo(() => buildEnergyTotals(prior3Insights, 0), [prior3Insights]);
  const prior4Energy = useMemo(() => buildEnergyTotals(prior4Insights, 0), [prior4Insights]);
  const trendSeries = useMemo(() => {
    const yearly = [
      { fy: `FY${year - 4}`, e: prior4Energy },
      { fy: `FY${year - 3}`, e: prior3Energy },
      { fy: `FY${year - 2}`, e: prior2Energy },
      { fy: `FY${year - 1}`, e: priorEnergy },
      { fy: `FY${year}`,     e: energy },
    ];
    const yearsWithAny = yearly.filter((p) => p.e && p.e.totalKwh > 0);
    if (yearsWithAny.length === 0) return [];
    const buildPoints = (key) => yearsWithAny.map((p) => ({ x: p.fy, y: Number(p.e[key]) || 0 }));
    return [
      { id: "Total",        data: buildPoints("totalKwh") },
      { id: "Electricity",  data: buildPoints("elecKwh") },
      { id: "Gas",          data: buildPoints("gasKwh") },
      { id: "Liquid fuels", data: buildPoints("liquidFuelsKwh") },
      { id: "Other",        data: buildPoints("otherKwh") },
    ];
  }, [year, prior4Energy, prior3Energy, prior2Energy, priorEnergy, energy]);

  // Carrier trend — matches the Section 1 cards exactly (Transport is a use,
  // not a carrier; it has its own breakdown in Section 3, not a trend line here).
  const TREND_SERIES = useMemo(() => [
    { key: "Total",        label: "Total",        color: SECR_PALETTE.emissionsLB, lightColor: "gray.100", textColor: "gray.700" },
    { key: "Electricity",  label: "Electricity",  color: SECR_PALETTE.electricity, lightColor: "blue.50",  textColor: "blue.700" },
    { key: "Gas",          label: "Gas",          color: SECR_PALETTE.gas,         lightColor: "green.50", textColor: "green.700" },
    { key: "Liquid fuels", label: "Liquid fuels", color: SECR_PALETTE.liquidFuels, lightColor: "orange.50", textColor: "orange.700" },
    { key: "Other",        label: "Other",        color: SECR_PALETTE.other,       lightColor: "purple.50", textColor: "purple.700" },
  ], []);
  const [visibleSeries, setVisibleSeries] = useState(() => new Set(TREND_SERIES.map((s) => s.key)));
  const toggleSeries = (key) => setVisibleSeries((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <Flex direction="column" gap={6}>
      <Flex justifyContent="flex-end" align="center" mt={2} gap={2} flexWrap="wrap">
        <UnitToggle unit={unit} setUnit={setUnit} />
        <DimensionDropdown selected={dimension || {}} setSelected={setDimension} />
        <YearDropdown setYear={setYear} selectedYear={year} />
        <QuarterDropdown setQuarter={setQuarter} selectedQuarter={quarter} />
      </Flex>

      <EnergyConsumptionSection
        current={energy}
        prior={priorEnergy}
        currentFy={fyLabel}
        priorFy={priorFy}
        hasData={hasEnergyData}
        kwhMissing={!!energy?.kwhMissing}
        trendSeries={trendSeries}
        seriesConfig={TREND_SERIES}
        visibleSeries={visibleSeries}
        toggleSeries={toggleSeries}
        view={energyView}
        setView={setEnergyView}
        year={year}
        dimensionParams={dimensionParams}
        unit={unit}
        isLoading={isLoading}
      />
      <ElectricityBreakdownSection rows={elecRows} totalKwh={elecTotalKwh} priorRows={priorElecRows} priorTotalKwh={priorElecTotalKwh} mixedV1V2={mixedV1V2Electricity} hiddenV1Kwh={hiddenV1ElecKwh} headlineKwh={energy?.elecKwh} priorHeadlineKwh={priorEnergy?.elecKwh} unit={unit} isLoading={isLoading} />
      <GasBreakdownSection rows={gasRows} totalKwh={gasTotalKwh} priorRows={priorGasRows} priorTotalKwh={priorGasTotalKwh} unit={unit} isLoading={isLoading} />
      <LiquidFuelsBreakdownSection rows={liquidFuelsRows} totalKwh={liquidFuelsTotalKwh} priorRows={priorLiquidFuelsRows} priorTotalKwh={priorLiquidFuelsTotalKwh} unit={unit} isLoading={isLoading} />
      {(isLoading || otherCarrierTotalKwh > 0) && (
        <OtherBreakdownSection rows={otherCarrierRows} totalKwh={otherCarrierTotalKwh} priorRows={priorOtherCarrierRows} priorTotalKwh={priorOtherCarrierTotalKwh} unit={unit} isLoading={isLoading} />
      )}
      <TransportFuelSection rows={fuelRows} totalKwh={transportTotalKwh} priorRows={priorFuelRows} priorTotalKwh={priorTransportTotalKwh} unit={unit} isLoading={isLoading} />
      <StationaryFuelSection rows={stationaryRows} totalKwh={stationaryTotalKwh} priorRows={priorStationaryRows} priorTotalKwh={priorStationaryTotalKwh} unit={unit} isLoading={isLoading} />
      <WaterSection
        supplyM3={supplyM3}
        dischargeM3={dischargeM3}
        priorSupplyM3={priorSupplyM3}
        priorDischargeM3={priorDischargeM3}
        supplyByQuarter={supplyByQuarter}
        dischargeByQuarter={dischargeByQuarter}
        priorSupplyByQuarter={priorSupplyByQuarter}
        priorDischargeByQuarter={priorDischargeByQuarter}
        priorFy={priorFy}
        isLoading={isLoading}
      />
      <WasteSection
        rows={wasteRoutes}
        totalTonnes={wasteTotalTonnes}
        priorRows={priorWasteRoutes}
        priorTotalTonnes={priorWasteTotalTonnes}
        items={wasteItems}
        isLoading={isLoading}
      />
      {isESGAdmin && (
        <IntensitySection
          current={energy}
          prior={priorEnergy}
          currentFy={fyLabel}
          priorFy={priorFy}
          supplyM3={supplyM3}
          wasteTotalTonnes={wasteTotalTonnes}
        />
      )}
      <OnsiteGenerationSection rows={onsiteRows} priorRows={priorOnsiteRows} unit={unit} isLoading={isLoading} />
    </Flex>
  );
};

export default SecrConsumptionDashboard;
